/* ==========================================================================
   Golden Wheel — interactive spin wheel
   - Reads segment config from <script data-wheel-config>
   - Fetches eligible orders + spin results via App Proxy (or mock in dev)
   - 3-phase canvas animation: accelerate → sustain-at-zoom → decelerate-tick
   ========================================================================== */
(() => {
  'use strict';

  const TAU = Math.PI * 2;
  const MAX_ZOOM = 3.2;             // how far we zoom in at the end
  const POINTER_TOP_INSET = 40;     // pixels from canvas top to pointer tip
  const SPIN_DURATION_MS = 6400;    // total animation duration
  const EXTRA_ROTATIONS = 6;        // full rotations on top of landing angle

  /* -------------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const lerp = (a, b, t) => a + (b - a) * t;

  /* Inline SVG voor het Gouden Lot icoon — gebruikt in JS-injected HTML
     (combo pill, golden-ticket pill, order badge). Mirror van het
     snippets/icon-golden-ticket.liquid zodat de visuele stijl 1-op-1
     matched. Unique gradient ID per call vermijdt id-collisions wanneer
     meerdere SVG's tegelijk in de DOM zitten. */
  let _ticketSvgCounter = 0;
  function ticketSvgInline() {
    const id = `gw-tkt-${++_ticketSvgCounter}`;
    return `<svg class="gw-ticket-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 40" aria-hidden="true" focusable="false"><defs><linearGradient id="${id}" x1="0" y1="0" x2="64" y2="40" gradientUnits="userSpaceOnUse"><stop offset="0%" stop-color="#9a722e"/><stop offset="50%" stop-color="#ffca70"/><stop offset="100%" stop-color="#9a722e"/></linearGradient></defs><path d="M6 6a3 3 0 0 1 3-3h46a3 3 0 0 1 3 3v6.5a4 4 0 0 0 0 15V34a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3v-6.5a4 4 0 0 0 0-15V6z" fill="url(#${id})" stroke="#44351c" stroke-width="1.2"/><line x1="40" y1="6" x2="40" y2="34" stroke="#44351c" stroke-width="1" stroke-dasharray="1.6 2" opacity="0.7"/><path d="M22 13l2.35 4.76 5.25 0.76-3.8 3.7 0.9 5.23L22 25l-4.7 2.45 0.9-5.23-3.8-3.7 5.25-0.76L22 13z" fill="#44351c"/></svg>`;
  }

  function hexToRgb(hex) {
    if (!hex) return { r: 74, g: 71, b: 65 };
    let h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return { r: 74, g: 71, b: 65 };
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
  }

  function rgbCss({ r, g, b }, a = 1) {
    return a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`;
  }

  // pct: -100 (black) to +100 (white). 0 = unchanged.
  function shadeRgb({ r, g, b }, pct) {
    const f = pct / 100;
    const mix = (c) => c + (f < 0 ? c * f : (255 - c) * f);
    return {
      r: Math.round(clamp(mix(r), 0, 255)),
      g: Math.round(clamp(mix(g), 0, 255)),
      b: Math.round(clamp(mix(b), 0, 255)),
    };
  }

  function desaturateRgb({ r, g, b }, amount = 0.9) {
    const gray = Math.round(0.3 * r + 0.59 * g + 0.11 * b);
    return {
      r: Math.round(lerp(r, gray, amount)),
      g: Math.round(lerp(g, gray, amount)),
      b: Math.round(lerp(b, gray, amount)),
    };
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function truncateToWidth(ctx, text, maxWidth) {
    if (!text) return '';
    if (ctx.measureText(text).width <= maxWidth) return text;
    let low = 0, high = text.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (ctx.measureText(text.slice(0, mid) + '…').width <= maxWidth) low = mid;
      else high = mid - 1;
    }
    return text.slice(0, low).trimEnd() + '…';
  }

  // Piecewise ease: slow-in → sustain → slow-out
  function easeSpinRotation(t) {
    if (t < 0.25) {
      const k = t / 0.25;
      return 0.12 * (k * k);
    }
    if (t < 0.55) {
      const k = (t - 0.25) / 0.30;
      return 0.12 + 0.48 * k;
    }
    const k = (t - 0.55) / 0.45;
    return 0.60 + 0.40 * (1 - Math.pow(1 - k, 3));
  }

  // Zoom ramps up over first ~70%, plateaus at MAX_ZOOM for final deceleration
  function easeZoom(t) {
    const k = clamp(t / 0.70, 0, 1);
    return 1 + (MAX_ZOOM - 1) * (1 - Math.pow(1 - k, 3));
  }

  function formatDate(isoString) {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString('nl-NL', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return isoString; }
  }

  /* -------------------------------------------------------------------------
     GoldenWheel
     ---------------------------------------------------------------------- */

  class GoldenWheel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-golden-wheel-stage]');
      this.canvas = root.querySelector('[data-wheel-canvas]');
      this.spinBtn = root.querySelector('[data-wheel-spin]');
      this.spinLabel = root.querySelector('[data-wheel-spin-label]');
      this.ordersEl = root.querySelector('[data-wheel-orders]');
      this.reveal = root.querySelector('[data-wheel-reveal]');
      this.historyEl = root.querySelector('[data-wheel-history]');
      this.historyBody = root.querySelector('[data-wheel-history-body]');

      this.proxyBase = root.dataset.proxyBase || '/apps/golden-wheel';
      this.devMock = root.dataset.devMock === 'true';

      this.state = {
        rotation: 0,
        zoom: 1,
        isSpinning: false,
        selectedOrderId: null,
        orders: [],
        eligibility: null,         // { windowValue, windowUnit, windowMs } from /config
        countdownInterval: null,   // 1s tick for live countdown on eligible orders
        tickets: null,             // total tickets the customer has earned
        goldMode: false,           // true when selected order is rigged → show banner + glow (rad-uitkomst onveranderd)
        teaserMode: false,         // true when customer has no eligible orders → masked wheel
        wedgeOpacity: 1,           // opacity multiplier for the wedges (used for fade-in)
        hasRendered: false,        // true once we've drawn real (portal) segments at least once
        activePool: null,          // 'standard' | 'premium' — welke pool nu op de wheel staat
        history: {
          spins: [],
          hasMore: false,
          nextBefore: null,
          loading: false,
          initialLoadDone: false,
          error: null,
        },
      };

      if (this.canvas) this.ctx = this.canvas.getContext('2d');
    }

    init() {
      if (!this.canvas) return;

      this.config = this.readConfig();
      this.isLoggedIn = !!(this.stage?.dataset?.customerId);
      // Start with NO segments — just frame + hub render until portal /config
      // resolves (logged-in) or the preview segments load (logged-out).
      this.segments = [];
      this.loadLogo();
      this.setupCanvas();
      this.bindEvents();
      this.draw();
      this.startShimmer();

      if (this.isLoggedIn) {
        this.loadRemoteConfig();
        this.handleDeepLink();
      } else {
        // Guest preview: 8 mystery wedges, all "?" placeholders so we
        // don't leak the actual prize pool to non-customers.
        this.state.canSeeProducts = false;
        this.config.segments = this.buildPreviewSegments();
        this.segments = this.generateSegments();
        this.fadeInWedges();
      }
    }

    /* 8 wedges alternating gold/black around the wheel, each with a "?"
       placeholder. All wedges share the same rarity so expandSegments'
       rarity-priority sort doesn't cluster the colours together — keeps
       the gold-zwart-gold-zwart pattern in order around the rim. */
    buildPreviewSegments() {
      return Array.from({ length: 8 }, (_, i) => ({
        id: `preview-${i}`,
        prizeId: `preview-${i}`,
        label: '',
        type: 'shopify_variant',
        rarity: 'rare', // same for all → stable order preserves alternation
        weight: 1,
        segments: 1,
        color: i % 2 === 0 ? '#ffca70' : '#0a0807',
        image: null,
        available: true,
        remaining: null,
      }));
    }

    /* Idle loop: continuous slow rotation + rare/legendary pulse-glow
       redraws. Runs at the device's native refresh rate via RAF for the
       smoothest possible drift. Pauses when:
         - a spin is running (spin's own RAF owns the draws)
         - the reveal/info modal is open (wheel hidden behind blur)
         - the tab is hidden (RAF auto-pauses anyway)
         - the wheel is scrolled offscreen (IntersectionObserver)
         - the user prefers reduced motion (accessibility) */
    startShimmer() {
      if (this._idleRAF != null) return;

      const reducedMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion) {
        // Single render — no animations.
        this.draw();
        return;
      }

      // Pause when scrolled out of view. Saves significant battery on
      // long pages where the wheel sits below the fold.
      this._inViewport = true;
      if ('IntersectionObserver' in window && this.stage) {
        this._intersectionObserver = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (e.target === this.stage) this._inViewport = e.isIntersecting;
          }
        }, { threshold: 0 });
        this._intersectionObserver.observe(this.stage);
      }

      const IDLE_ROTATE_PER_SEC = TAU / 55; // 1 full rotation per 55s
      let lastTime = performance.now();

      const tick = (now) => {
        // dt clamp guards against tab-unsuspend producing huge jumps
        const dt = Math.min(0.08, (now - lastTime) / 1000);
        lastTime = now;

        const isHidden = document.visibilityState !== 'visible';
        const isRevealOpen = this.reveal?.hasAttribute('data-open')
          || this.infoModal?.hasAttribute('data-open');

        if (!this.state.isSpinning && !isHidden && !isRevealOpen
            && this._inViewport !== false) {
          this.state.rotation += IDLE_ROTATE_PER_SEC * dt;
          this.draw();
        }

        this._idleRAF = requestAnimationFrame(tick);
      };
      this._idleRAF = requestAnimationFrame(tick);
    }

    /* Fetch live config from portal via App Proxy.
       Updates prize availability (greys out unavailable prizes on canvas)
       and merges eligibleOrders over the Liquid-rendered ones. */
    async loadRemoteConfig() {
      const debugMeta = document.querySelector('[data-wheel-debug-meta]');
      const debugBody = document.querySelector('[data-wheel-debug-body]');
      const setDebug = (meta, body) => {
        if (debugMeta) debugMeta.textContent = meta;
        if (debugBody) debugBody.textContent = body;
      };

      const url = `${this.proxyBase}/config`;
      const t0 = performance.now();
      setDebug(`→ GET ${url}`, 'Wachten op response…');

      let res, bodyText, data;
      try {
        res = await fetch(url, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
        });
      } catch (err) {
        const ms = Math.round(performance.now() - t0);
        setDebug(`❌ Netwerkfout · ${ms}ms`, `${err.name}: ${err.message}\n\nMogelijke oorzaken:\n- Portal draait niet op portal.cnscoops.nl\n- App Proxy niet correct geconfigureerd in Shopify Partner Dashboard\n- CORS / mixed content`);
        console.warn('[golden-wheel] /config fetch failed:', err);
        this.renderOrdersError(err);
        this.fallbackToLiquidSegments();
        return;
      }

      const ms = Math.round(performance.now() - t0);
      const contentType = res.headers.get('content-type') || '(geen content-type)';

      try {
        bodyText = await res.text();
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch (err) {
        setDebug(
          `⚠️ ${res.status} ${res.statusText} · ${ms}ms · niet-JSON response`,
          `Content-Type: ${contentType}\n\nResponse body (eerste 2000 chars):\n${(bodyText || '').slice(0, 2000)}`
        );
        console.warn('[golden-wheel] /config not JSON:', err);
        return;
      }

      const statusIcon = res.ok ? '✅' : '❌';
      setDebug(
        `${statusIcon} ${res.status} ${res.statusText} · ${ms}ms · ${contentType}`,
        JSON.stringify(data, null, 2)
      );
      console.log('[golden-wheel] remote config:', data);

      if (!res.ok || !data) return;

      // Portal beheert nu TWEE prize-pools:
      //   data.prizes        → standard pool (orders < garantie-drempel)
      //   data.premiumPrizes → premium pool (orders ≥ garantie-drempel,
      //                        alleen producten — geen tickets)
      // Welke we tonen hangt af van de geselecteerde order's
      // riggedToGoldenTicket flag (zie applyPool / selectOrder).
      if (Array.isArray(data.prizes) || Array.isArray(data.premiumPrizes)) {
        this.config.standardSegments = (data.prizes || []).map(p => this.mapPrizeToSegment(p));
        this.config.premiumSegments  = (data.premiumPrizes || []).map(p => this.mapPrizeToSegment(p));

        // Preload images uit BEIDE pools zodat een pool-switch instant is.
        this.preloadPoolImages(this.config.standardSegments);
        this.preloadPoolImages(this.config.premiumSegments);

        // Initiële pool: kies op basis van eerste eligible order — anders
        // standard. selectOrder zal 'm later eventueel switchen.
        const firstOrder = Array.isArray(data.eligibleOrders) ? data.eligibleOrders[0] : null;
        const initialPool = firstOrder?.riggedToGoldenTicket ? 'premium' : 'standard';
        this.applyPool(initialPool, { silent: true });
      }

      // Eligibility window + drempels (for live countdown + admin-built empty-state copy)
      if (data.eligibility && typeof data.eligibility === 'object') {
        this.state.eligibility = data.eligibility;
      }

      // Server-built empty-state message — reflects live admin drempels.
      // Falls back to local emptyStateHint() if missing (older portal).
      if (typeof data.emptyStateMessage === 'string') {
        this.state.emptyStateMessage = data.emptyStateMessage;
      }

      // Ticket counter — comes back in every config + spin response
      this.applyTicketsFromResponse(data);

      // Portal is the single source of truth for orders. Backend pre-filtert:
      // ALREADY_SPUN / WINDOW_EXPIRED / BELOW_MIN_AMOUNT zijn al weggehaald.
      // Single-spin model: één keer draaien per order, klaar. Geen
      // bonus-chain meer om over reloads heen te restoren.
      if (Array.isArray(data.eligibleOrders)) {
        this.state.orders = data.eligibleOrders;
        this.renderOrders();
        this.startCountdownTicker();

        const first = data.eligibleOrders[0];
        if (first) {
          this.selectOrder(first.id);
        } else {
          this.setSpinIdleLabel();
        }
      }

      if (data.active === false) {
        this.showInactiveState();
      }

      // Spin-history laden (eerste keer) — gebeurt async, blokkeert
      // de wheel-render niet. Backend levert lege array als de klant
      // nog niets gedraaid heeft → sectie blijft hidden.
      if (!this.state.history.initialLoadDone) {
        this.fetchHistory();
      }
    }

    showInactiveState() {
      this.stage.innerHTML = `
        <div class="golden-wheel__orders-state" style="padding:80px 24px;text-align:center;">
          <span>Dit event is afgelopen. Bedankt voor het meedoen!</span>
        </div>`;
    }

    /* If portal /config fails (network error, CORS, dev preview without
       backend), fall back to whatever blocks the merchant configured in
       Liquid. Useful for theme-editor preview, never reached in prod. */
    fallbackToLiquidSegments() {
      if (this.state.hasRendered) return; // already showing real data
      const liquid = this.readConfig();
      if (!liquid?.segments?.length) return;
      this.config.segments = liquid.segments;
      this.segments = this.generateSegments();
      this.fadeInWedges();
    }

    /* Smooth opacity tween 0 → 1 over ~400ms when wedges first arrive
       on the canvas. The frame (rim, hub) was already drawn so only the
       wedges and labels fade in. */
    fadeInWedges() {
      this.state.hasRendered = true;
      if (this._wedgeFadeRAF) cancelAnimationFrame(this._wedgeFadeRAF);
      const startTime = performance.now();
      const duration = 420;
      const tick = (now) => {
        const t = clamp((now - startTime) / duration, 0, 1);
        // Ease out cubic
        this.state.wedgeOpacity = 1 - Math.pow(1 - t, 3);
        this.draw();
        if (t < 1) this._wedgeFadeRAF = requestAnimationFrame(tick);
      };
      this._wedgeFadeRAF = requestAnimationFrame(tick);
    }

    /* Map a portal prize object to a wheel-segment config.
       For shopify_variant prizes prefer the shorter productTitle since
       the full label ("Whey Protein — Aardbei 1kg") is too long for a
       wedge. `segments` is the count of evenly-spaced stripes the prize
       should occupy on the wheel; `weight` is the total tile count. */
    mapPrizeToSegment(p) {
      const v = p.shopifyVariant;
      const isVariant = p.type === 'shopify_variant' && v;
      const label = isVariant ? (v.productTitle || p.label || '') : (p.label || '');
      return {
        id: p.id,
        prizeId: p.id,
        label,
        type: p.type || 'custom',
        rarity: p.rarity || 'common',
        weight: p.weight != null ? p.weight : 1,
        segments: p.segments != null ? p.segments : 1,
        color: p.color || '#4a4741',
        image: v?.image || p.image || null,
        handle: v?.handle || null,
        description: p.description || '',
        available: p.available !== false,
        remaining: p.remaining,
      };
    }

    /* Read the ticket total from any response shape (config / spin /
       lightweight /tickets) and update the sticky counter. The spin
       endpoint returns `customerTickets` — accept synonyms so we don't
       break if the field is renamed upstream. */
    applyTicketsFromResponse(payload) {
      if (!payload) return;
      const t = payload.customerTickets
             ?? payload.tickets
             ?? payload.ticketTotal
             ?? payload.ticketCount;
      if (typeof t === 'number' && Number.isFinite(t)) {
        this.state.tickets = t;
        this.renderTickets();
      }
    }

    /* Render the ticket pill. By default a count increase triggers the
       +1 pulse animation; pass { silent: true } for sanity-syncs that
       shouldn't draw the eye (e.g. catching up to portal mid-flow). */
    renderTickets(opts = {}) {
      const wrap = this.root.querySelector('[data-wheel-tickets]');
      const countEl = this.root.querySelector('[data-wheel-tickets-count]');
      if (!wrap || !countEl) return;
      if (this.state.tickets == null) {
        wrap.hidden = true;
        return;
      }

      const prev = Number.parseInt(countEl.textContent, 10);
      const next = this.state.tickets;
      countEl.textContent = String(next);
      const labelEl = this.root.querySelector('[data-wheel-tickets-label]');
      if (labelEl) {
        labelEl.textContent = next === 1 ? 'lot verzameld' : 'loten verzameld';
      }
      wrap.hidden = false;

      if (!opts.silent && Number.isFinite(prev) && next > prev) {
        wrap.classList.remove('is-pulsing');
        // force reflow so the animation restarts when the class re-attaches
        void wrap.offsetWidth;
        wrap.classList.add('is-pulsing');
        clearTimeout(this._ticketPulseTimer);
        this._ticketPulseTimer = setTimeout(
          () => wrap.classList.remove('is-pulsing'),
          900
        );
      }
    }

    /* Set the ticket count directly. Used by the spin flow which times
       the visual update to match the wheel's landing animation. */
    setTickets(value, opts = {}) {
      if (typeof value !== 'number' || !Number.isFinite(value)) return;
      this.state.tickets = value;
      this.renderTickets(opts);
    }

    /* Lightweight ticket-only fetch for use outside the rad-flow (account
       pages, header widget, etc.). On the rad page itself /config is
       enough since it carries the count too. Exposed on the instance so
       external code can call it: `goldenWheelInstance.fetchTickets()`. */
    async fetchTickets() {
      try {
        const data = await this.proxyCall('/tickets');
        this.applyTicketsFromResponse(data);
        return this.state.tickets;
      } catch (err) {
        console.warn('[golden-wheel] /tickets fetch failed:', err);
        return null;
      }
    }

    /* Pre-spin eligibility check — re-fetches /config and confirms the
       selected order is still in the eligibleOrders list. This is a UX
       safety net; portal's atomic INSERT ON CONFLICT is the real
       race-protection. */
    async verifyEligibility(orderId) {
      try {
        const data = await this.proxyCall('/config');
        if (!data || data.active === false) return false;

        // Sync BEIDE pools (standard + premium) zodat UI matched met
        // portal. Replace op any array response (empty allowed). Force
        // re-apply van de huidige actieve pool zodat de wheel direct de
        // verse data toont (anders zou applyPool een no-op zijn).
        if (Array.isArray(data.prizes) || Array.isArray(data.premiumPrizes)) {
          this.config.standardSegments = (data.prizes || []).map(p => this.mapPrizeToSegment(p));
          this.config.premiumSegments  = (data.premiumPrizes || []).map(p => this.mapPrizeToSegment(p));
          this.preloadPoolImages(this.config.standardSegments);
          this.preloadPoolImages(this.config.premiumSegments);
          // Force re-apply van de huidige actieve pool: tijdelijk reset
          // activePool zodat applyPool niet eary-return't.
          const currentPool = this.state.activePool || 'standard';
          this.state.activePool = null;
          this.applyPool(currentPool, { silent: true });
        }
        if (data.eligibility && typeof data.eligibility === 'object') {
          this.state.eligibility = data.eligibility;
        }
        if (typeof data.emptyStateMessage === 'string') {
          this.state.emptyStateMessage = data.emptyStateMessage;
        }
        this.applyTicketsFromResponse(data);
        if (Array.isArray(data.eligibleOrders)) {
          this.state.orders = data.eligibleOrders;
          this.renderOrders();
          this.startCountdownTicker();
        }

        return (data.eligibleOrders || []).some(o => String(o.id) === String(orderId));
      } catch (err) {
        // If the pre-flight itself fails, let the spin attempt proceed —
        // portal will still return ALREADY_SPUN / ORDER_NOT_ELIGIBLE.
        console.warn('[golden-wheel] pre-flight config failed, proceeding:', err);
        return true;
      }
    }

    readConfig() {
      const tag = document.querySelector('[data-wheel-config]');
      try { return JSON.parse(tag.textContent); }
      catch { return { segments: [] }; }
    }

    /* Tile-based wheel layout per portal v3 spec.

       Each prize has `weight` (total tiles) and `segments` (number of
       evenly-spaced stripes). E.g. weight=10, segments=5 → five 2-tile
       stripes placed at 0%, 20%, 40%, 60%, 80% of the wheel, NOT one
       contiguous 10-tile chunk.

       The portal owns colours, so we render `prize.color` verbatim
       (golden_ticket type uses the radial gradient in drawWedge).

       Rigged orders veranderen het rad NIET visueel — uitkomst is
       altijd weighted random over de volledige prize-pool. De garantie
       op een Golden Ticket bovenop de win wordt gecommuniceerd via de
       rigged-banner boven het rad (zie renderRiggedBanner). */
    generateSegments() {
      const raw = (this.config.segments || []).filter(s => s && s.prizeId);
      if (!raw.length) return [];

      const tiles = this.expandSegments(raw);
      return this.tilesToStripes(tiles);
    }

    /* Tile expansion. Returns an array of length = sum(weights), each
       slot pointing at the prize that occupies that tile. Rarer prizes
       are placed first so they get their exact target positions; later
       prizes shift forward on collision. */
    expandSegments(prizes) {
      const totalTiles = prizes.reduce((s, p) => s + Math.max(1, p.weight || 1), 0);
      const tiles = new Array(totalTiles).fill(null);

      const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };
      const ordered = [...prizes].sort((a, b) =>
        (rarityOrder[a.rarity] ?? 3) - (rarityOrder[b.rarity] ?? 3)
      );

      for (const prize of ordered) {
        const weight = Math.max(1, prize.weight || 1);
        const segCount = Math.max(1, Math.min(prize.segments || 1, weight));
        const baseStripeSize = Math.floor(weight / segCount);
        const remainder = weight - baseStripeSize * segCount;
        const spacing = totalTiles / segCount;

        for (let i = 0; i < segCount; i++) {
          const stripeLen = baseStripeSize + (i < remainder ? 1 : 0);
          const targetStart = Math.round(i * spacing);

          // Find empty slot at or after the target
          let pos = targetStart;
          let attempts = 0;
          while (tiles[pos % totalTiles] !== null && attempts < totalTiles) {
            pos++;
            attempts++;
          }

          for (let j = 0; j < stripeLen; j++) {
            const slot = (pos + j) % totalTiles;
            if (tiles[slot] === null) tiles[slot] = prize;
          }
        }
      }

      // Backfill any leftover slots (shouldn't happen if weights are sane)
      const fallback = ordered[ordered.length - 1] || prizes[0];
      for (let i = 0; i < totalTiles; i++) {
        if (tiles[i] === null) tiles[i] = fallback;
      }

      return tiles;
    }

    /* Group consecutive same-prize tiles into wedge "stripes" with
       computed angles. Wraps around the 0/360° boundary. */
    tilesToStripes(tiles) {
      const total = tiles.length;
      if (!total) return [];

      const tileAngle = TAU / total;
      const stripes = [];
      let i = 0;
      while (i < total) {
        const prize = tiles[i];
        let j = i + 1;
        while (j < total && tiles[j] === prize) j++;

        const length = j - i;
        const startAngle = i * tileAngle;
        const endAngle = j * tileAngle;
        stripes.push({
          id: `${prize.id}__${i}`,
          prizeId: prize.prizeId,
          label: prize.label || '',
          type: prize.type || 'custom',
          rarity: prize.rarity || 'common',
          color: prize.color || '#4a4741',
          image: prize.image || null,
          handle: prize.handle || null,
          available: prize.available !== false,
          remaining: prize.remaining,
          startAngle,
          endAngle,
          midAngle: (startAngle + endAngle) / 2,
          size: endAngle - startAngle,
          tileStart: i,
          tileLength: length,
        });
        i = j;
      }

      // Wrap-merge: if first and last stripe share the same prize they
      // visually connect across 0°, render as one continuous wedge.
      if (stripes.length > 1
          && stripes[0].prizeId === stripes[stripes.length - 1].prizeId) {
        const last = stripes.pop();
        stripes[0].startAngle = last.startAngle - TAU;
        stripes[0].midAngle = (stripes[0].startAngle + stripes[0].endAngle) / 2;
        stripes[0].size = stripes[0].endAngle - stripes[0].startAngle;
        stripes[0].tileStart = last.tileStart;
        stripes[0].tileLength += last.tileLength;
      }

      return stripes;
    }

    loadLogo() {
      const url = this.stage?.dataset?.logoUrl;
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        this.logoImg = img;
        this.draw();
      };
      img.onerror = () => { this.logoImg = null; };
      img.src = url;
    }

    /* Preload product thumbnails for shopify_variant prizes so they're
       ready by the time we draw. No crossOrigin so Shopify CDN images
       load regardless of CORS headers — we only drawImage, never
       getImageData, so canvas tainting is a non-issue. */
    loadWedgeImages() {
      if (!this.imageCache) this.imageCache = new Map();
      for (const seg of this.segments || []) {
        const url = seg.image;
        if (!url || this.imageCache.has(url)) continue;
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => { this.imageCache.set(url, img); this.draw(); };
        img.onerror = () => {
          console.warn('[golden-wheel] image failed to load:', url);
          this.imageCache.set(url, null);
        };
        this.imageCache.set(url, img);   // mark as loading
        img.src = url;
      }
    }

    /* Preload images uit een willekeurig pool-segment array (zonder dat
       die pool actief hoeft te zijn). Gebruik bij /config zodat zowel
       standard als premium tegels al in cache zitten — pool-switch is
       dan instant zonder image-load delay. */
    preloadPoolImages(poolSegments) {
      if (!this.imageCache) this.imageCache = new Map();
      for (const seg of poolSegments || []) {
        const url = seg.image;
        if (!url || this.imageCache.has(url)) continue;
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => { this.imageCache.set(url, img); this.draw(); };
        img.onerror = () => { this.imageCache.set(url, null); };
        this.imageCache.set(url, img);
        img.src = url;
      }
    }

    /* Switch active prize-pool (standard ↔ premium). No-op als de pool
       al actief is. Regenereert segments + redraws + fade-in.
       options.silent skipt de fade-in (gebruikt bij initial /config load
       zodat de hoofd-fadeIn van de wheel niet dubbel triggert). */
    applyPool(poolName, { silent = false } = {}) {
      if (poolName !== 'standard' && poolName !== 'premium') return;
      if (this.state.activePool === poolName) return;

      const source = poolName === 'premium'
        ? this.config.premiumSegments
        : this.config.standardSegments;

      // Defensive: empty pool kan voorkomen als admin nog niets heeft
      // ingesteld in de premium pool. Server zet dan riggedToGoldenTicket
      // op false, dus we komen hier idealiter niet — maar fall back naar
      // standard zodat de wheel nooit blanco staat.
      if (!source || source.length === 0) {
        if (poolName === 'premium' && this.config.standardSegments?.length) {
          return this.applyPool('standard', { silent });
        }
        return;
      }

      this.state.activePool = poolName;
      this.config.segments = source;
      this.segments = this.generateSegments();
      this.loadWedgeImages();
      if (silent) {
        this.draw();
      } else {
        this.fadeInWedges();
      }
    }


    setupCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.stage.getBoundingClientRect();
      const size = Math.floor(Math.min(rect.width, rect.height));

      this.canvas.width = size * dpr;
      this.canvas.height = size * dpr;
      this.canvas.style.width = size + 'px';
      this.canvas.style.height = size + 'px';

      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.size = size;
      this.radius = (size - POINTER_TOP_INSET * 2) / 2;
    }

    bindEvents() {
      this.spinBtn?.addEventListener('click', () => this.spin());

      this.reveal?.querySelector('[data-wheel-reveal-close]')
        ?.addEventListener('click', () => this.closeReveal());

      // Info modal — opens "what can I win" prize-list popup
      this.infoModal = this.root.querySelector('[data-wheel-info]');
      this.root.querySelector('[data-wheel-info-open]')
        ?.addEventListener('click', () => this.openInfoModal());
      this.infoModal?.querySelector('[data-wheel-info-close]')
        ?.addEventListener('click', () => this.closeInfoModal());
      this.infoModal?.addEventListener('click', (e) => {
        if (e.target === this.infoModal) this.closeInfoModal();
      });

      // Gouden Lot modal — opent vanuit step 3 in de "Hoe werkt het" sectie
      this.goudenLotModal = this.root.querySelector('[data-wheel-gouden-lot]');
      this.root.querySelectorAll('[data-wheel-gouden-lot-open]').forEach(btn => {
        btn.addEventListener('click', () => this.openGoudenLotModal());
      });
      this.goudenLotModal?.querySelector('[data-wheel-gouden-lot-close]')
        ?.addEventListener('click', () => this.closeGoudenLotModal());
      this.goudenLotModal?.addEventListener('click', (e) => {
        if (e.target === this.goudenLotModal) this.closeGoudenLotModal();
      });

      document.querySelector('[data-wheel-debug-copy]')
        ?.addEventListener('click', (e) => this.copyDebug(e.currentTarget));

      // Close modal on backdrop click
      this.reveal?.addEventListener('click', (e) => {
        if (e.target === this.reveal) this.closeReveal();
      });

      // Debounced resize handler (window fallback)
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.setupCanvas();
          this.draw();
        }, 150);
      });

      // ResizeObserver on the stage catches container resizes that don't
      // fire a window resize event — iframe reflow, theme editor device
      // switch, flexbox reshuffle. Keeps canvas pixel-accurate when the
      // stage grows/shrinks.
      if ('ResizeObserver' in window && this.stage) {
        let roTimer;
        this._resizeObserver = new ResizeObserver(() => {
          clearTimeout(roTimer);
          roTimer = setTimeout(() => {
            this.setupCanvas();
            this.draw();
          }, 60);
        });
        this._resizeObserver.observe(this.stage);
      }

      // Escape key closes whichever modal is open. Standard a11y.
      this._escHandler = (e) => {
        if (e.key !== 'Escape') return;
        if (this.reveal?.hasAttribute('data-open')) {
          this.closeReveal();
        } else if (this.infoModal?.hasAttribute('data-open')) {
          this.closeInfoModal();
        } else if (this.goudenLotModal?.hasAttribute('data-open')) {
          this.closeGoudenLotModal();
        }
      };
      document.addEventListener('keydown', this._escHandler);

      // Re-fetch config when user returns to tab — keeps list fresh
      // without needing polling. Throttle to once per 10s. Guest mode
      // has nothing to fetch, so skip.
      let lastFocusRefresh = 0;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (this.state.isSpinning) return;
        if (!this.isLoggedIn) return;
        const now = Date.now();
        if (now - lastFocusRefresh < 10_000) return;
        lastFocusRefresh = now;
        this.loadRemoteConfig();
      });
    }

    /* ---------------------------------------------------------------------
       Backend calls (via App Proxy)
       When dev_mock=true, uses in-page mock so you can test without backend.
       ------------------------------------------------------------------ */

    /* Returns the parsed JSON body. On non-2xx, throws an Error whose
       `.status` is the HTTP status and `.body` is the parsed JSON so the
       caller can read `error`, `windowValue`, etc. */
    async proxyCall(path, body) {
      const url = `${this.proxyBase}${path}`;
      const opts = {
        method: body ? 'POST' : 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      };
      if (body) {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(url, opts);
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const err = new Error(`Proxy ${res.status}`);
        err.status = res.status;
        err.body = data;
        throw err;
      }
      return data;
    }


    /* ---------------------------------------------------------------------
       Orders list rendering
       ------------------------------------------------------------------ */

    renderOrders() {
      const orders = this.state.orders || [];
      const hasOrders = orders.length > 0;
      // Track whether the customer is allowed to see product details +
      // whether we should show the teaser-rad (no eligible orders).
      // Re-render the wheel when either flips so placeholders + gold
      // background swap in/out of the existing wedges. We do NOT
      // regenerate segments — drawWedge / getWedgeFill consult these
      // flags at draw-time, so the wheel layout stays stable.
      const prevCanSee = this.state.canSeeProducts;
      const prevTeaser = this.state.teaserMode;
      this.state.canSeeProducts = hasOrders;
      this.state.teaserMode = !hasOrders;
      if (prevCanSee !== hasOrders || prevTeaser !== this.state.teaserMode) {
        this.draw();
      }

      // No orders → server-built empty-state melding (admin drempels reflecting).
      // Fallback voor oudere portal-versies die geen emptyStateMessage sturen.
      if (!hasOrders) {
        const message = this.state.emptyStateMessage
          || this.emptyStateHint()
          || 'Geen geldige bestelling beschikbaar.';
        const wrap = document.createElement('div');
        wrap.className = 'golden-wheel__orders-state';
        const span = document.createElement('span');
        span.textContent = message;
        wrap.appendChild(span);
        this.ordersEl.replaceChildren(wrap);
        return;
      }

      // Chronological only — newest first. Backend pre-filtert, dus
      // alles wat we hier krijgen is draaibaar.
      const sorted = [...orders].sort((a, b) =>
        String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
      );

      this.ordersEl.innerHTML = sorted.map(order => this.renderOrderItem(order)).join('');

      this.ordersEl.querySelectorAll('[data-select-order]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.selectOrder(e.currentTarget.dataset.selectOrder);
          this.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }

    /* Fallback empty-state hint voor als portal geen emptyStateMessage stuurt
       (oudere versie of dev mock). Server-side melding heeft voorrang. */
    emptyStateHint() {
      const e = this.state.eligibility;
      if (!e || !e.windowValue) return '';
      return `Heb je een order geplaatst? Dan kan je binnen ${this.formatWindow(e.windowValue, e.windowUnit)} na jouw aankoop draaien.`;
    }

    renderOrderItem(order) {
      // Beknopt: alleen datum + bedrag, geen item-count
      const metaParts = [formatDate(order.createdAt)];
      if (order.totalPrice) metaParts.push(order.totalPrice);

      // Backend filtert ineligibles weg → alle orders zijn draaibaar.
      const deadline = this.getOrderDeadline(order);
      const remaining = deadline ? deadline - Date.now() : null;
      const countdownHtml = remaining != null && remaining > 0 ? `
        <p class="golden-wheel__order-countdown" data-order-countdown="${order.id}">
          ⏱ ${this.formatCountdown(remaining)}
        </p>` : '';
      const goldenBadge = order.riggedToGoldenTicket
        ? `<span class="golden-wheel__order-badge">${ticketSvgInline()} Gouden Lot</span>`
        : '';
      const rowClass = order.riggedToGoldenTicket
        ? 'golden-wheel__order is-golden'
        : 'golden-wheel__order';

      return `
        <div class="${rowClass}" data-order-id="${order.id}">
          <div class="golden-wheel__order-meta">
            <p class="golden-wheel__order-name">${order.name}${goldenBadge}</p>
            <p class="golden-wheel__order-date">${metaParts.join(' · ')}</p>
            ${countdownHtml}
          </div>
          <button type="button" class="golden-wheel__order-action"
                  data-select-order="${order.id}">
            Kies
          </button>
        </div>
      `;
    }

    /* ---------------------------------------------------------------------
       Spin history — "Eerder gedraaid"
       --------------------------------------------------------------------- */

    /* Fetch + paginate via portal /history. before = ISO cursor uit de
       vorige response (alleen bij "Laad meer"). Idempotent: dubbele calls
       tijdens loading worden gedropt. */
    async fetchHistory(before = null) {
      if (!this.historyEl || !this.historyBody) return;
      const h = this.state.history;
      if (h.loading) return;

      h.loading = true;
      h.error = null;
      this.renderHistory();

      try {
        const params = new URLSearchParams({ limit: '20' });
        if (before) params.set('before', before);
        const url = `${this.proxyBase}/history?${params.toString()}`;

        const res = await fetch(url, {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
        });

        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.ok) {
          throw new Error((data && data.error) || `HTTP ${res.status}`);
        }

        const newSpins = Array.isArray(data.spins) ? data.spins : [];
        h.spins = before ? [...h.spins, ...newSpins] : newSpins;
        h.hasMore = !!data.hasMore;
        h.nextBefore = data.nextBefore || null;
      } catch (err) {
        h.error = 'Kon spin-historie niet laden.';
        console.warn('[golden-wheel] /history fetch failed:', err);
      } finally {
        h.loading = false;
        h.initialLoadDone = true;
        this.renderHistory();
      }
    }

    /* Prepend een nieuwe spin (na succesvolle /spin) zodat de history
       direct ge-update is zonder full refetch. Backend is canonical bij
       de volgende natuurlijke reload. */
    prependHistorySpin(spin) {
      if (!spin || !this.state.history.initialLoadDone) return;
      // Skip als spin al bestaat (idempotent — bv. bij race met refetch)
      const exists = this.state.history.spins.some(s => s.spinId === spin.spinId);
      if (exists) return;
      this.state.history.spins = [spin, ...this.state.history.spins];
      this.renderHistory();
    }

    renderHistory() {
      if (!this.historyEl || !this.historyBody) return;
      const { spins, hasMore, loading, error, initialLoadDone } = this.state.history;

      // Voor de allereerste load: laat 'm hidden tot we iets te tonen
      // hebben (zelfs een lege state). Geen onnodige skeleton-flash.
      if (!initialLoadDone && !loading) {
        this.historyEl.hidden = true;
        return;
      }

      // Initial loading skeleton
      if (loading && !spins.length) {
        this.historyEl.hidden = false;
        this.historyBody.innerHTML = `
          <div class="golden-wheel__history-state">
            <span class="golden-wheel__spinner" aria-hidden="true"></span>
            <span>Eerdere draaien laden…</span>
          </div>
        `;
        return;
      }

      // Empty + error states
      if (!spins.length) {
        if (error) {
          this.historyEl.hidden = false;
          this.historyBody.innerHTML = `
            <div class="golden-wheel__history-state golden-wheel__history-state--error">
              ⚠️ ${escapeHtml(error)}
            </div>
          `;
          return;
        }
        // Geen geschiedenis → sectie blijft hidden, geen "lege" header
        this.historyEl.hidden = true;
        return;
      }

      // List
      this.historyEl.hidden = false;
      const cards = spins.map(s => this.renderHistoryCard(s)).join('');

      const loadMore = hasMore
        ? `<button type="button" class="golden-wheel__history-load-more"
                   data-wheel-history-load-more
                   ${loading ? 'disabled' : ''}>
             ${loading ? 'Laden…' : 'Laad meer'}
           </button>`
        : '';

      const errorBanner = error
        ? `<div class="golden-wheel__history-state golden-wheel__history-state--error">
             ⚠️ ${escapeHtml(error)}
           </div>`
        : '';

      this.historyBody.innerHTML = `
        <ul class="golden-wheel__history-list">${cards}</ul>
        ${errorBanner}
        ${loadMore}
      `;

      this.historyBody.querySelector('[data-wheel-history-load-more]')
        ?.addEventListener('click', () => {
          if (this.state.history.nextBefore) {
            this.fetchHistory(this.state.history.nextBefore);
          }
        });
    }

    renderHistoryCard(spin) {
      const prize = spin.prize || {};
      const bonus = spin.bonusPrize || null;
      const hasBonusPrize = !!bonus;
      const isPureTicket = prize.type === 'golden_ticket';
      const isCombo = !isPureTicket && !!spin.grantsGoldenTicket;
      const rarity = prize.rarity || 'common';
      const label = prize.label || 'Onbekende prijs';
      const variant = prize.shopifyVariant;

      // Visual: product-foto bij shopify_variant met image, anders ticket
      const visualHtml = (prize.type === 'shopify_variant' && variant?.image)
        ? `<div class="golden-wheel__history-card-visual">
             <img src="${escapeHtml(variant.image)}" alt="" loading="lazy" />
           </div>`
        : `<div class="golden-wheel__history-card-visual golden-wheel__history-card-visual--ticket">
             ${ticketSvgInline()}
           </div>`;

      const variantLine = variant?.variantTitle
        ? `<p class="golden-wheel__history-card-variant">${escapeHtml(variant.variantTitle)}</p>`
        : '';

      // Bonus badge: toont de ANDERE prijs uit de combo. Twee gevallen:
      //  - prize=ticket  + bonusPrize=product → "+ Bonus: <productlabel>"
      //  - prize=product + grantsGoldenTicket → "+ 🎫 Gouden Lot"
      let comboBadge = '';
      if (isPureTicket && hasBonusPrize) {
        const bonusLabel = bonus.label || 'Bonus prijs';
        comboBadge = `<span class="golden-wheel__history-card-combo">+&nbsp;Bonus: ${escapeHtml(bonusLabel)}</span>`;
      } else if (isCombo) {
        comboBadge = `<span class="golden-wheel__history-card-combo">+&nbsp;${ticketSvgInline()} Gouden Lot</span>`;
      }

      const orderName = spin.orderName ? escapeHtml(spin.orderName) : '';
      const dateLabel = this.formatRelativeDate(spin.spunAt);

      return `
        <li class="golden-wheel__history-card golden-wheel__history-card--${escapeHtml(rarity)}">
          ${visualHtml}
          <div class="golden-wheel__history-card-body">
            <p class="golden-wheel__history-card-meta">
              ${orderName ? `<span class="golden-wheel__history-card-order">${orderName}</span>` : ''}
              ${orderName && dateLabel ? `<span class="golden-wheel__history-card-sep" aria-hidden="true">·</span>` : ''}
              ${dateLabel ? `<span class="golden-wheel__history-card-date">${escapeHtml(dateLabel)}</span>` : ''}
            </p>
            <p class="golden-wheel__history-card-label">${escapeHtml(label)}</p>
            ${variantLine}
            ${comboBadge}
          </div>
        </li>
      `;
    }

    /* "Vandaag" / "Gisteren" / "X dagen geleden" / "X weken geleden" /
       voluit datum als ouder dan 30 dagen. */
    formatRelativeDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const diffMs = Date.now() - d.getTime();
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffDays <= 0) return 'Vandaag';
      if (diffDays === 1) return 'Gisteren';
      if (diffDays < 7) return `${diffDays} dagen geleden`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;
      try {
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch { return iso; }
    }

    /* Countdown helpers
       ------------------------------------------------------------------ */

    getOrderDeadline(order) {
      if (!order || !order.createdAt) return null;
      const win = this.state.eligibility?.windowMs;
      if (!win || win <= 0) return null;
      const created = new Date(order.createdAt).getTime();
      if (!created) return null;
      return created + win;
    }

    formatCountdown(ms) {
      if (ms <= 0) return 'verlopen';
      const totalSec = Math.floor(ms / 1000);
      if (totalSec < 60) return `nog ${totalSec}s`;
      const totalMin = Math.floor(totalSec / 60);
      if (totalMin < 60) return `nog ${totalMin} min`;
      const totalHour = Math.floor(totalMin / 60);
      if (totalHour < 24) {
        const remMin = totalMin % 60;
        return `nog ${totalHour}u ${remMin}m`;
      }
      const days = Math.floor(totalHour / 24);
      const remHour = totalHour % 24;
      return `nog ${days}d ${remHour}u`;
    }

    formatWindow(value, unit) {
      const unitMap = {
        minutes: value === 1 ? 'minuut' : 'minuten',
        hours:   value === 1 ? 'uur' : 'uur',
        days:    value === 1 ? 'dag' : 'dagen',
      };
      const unitStr = unitMap[unit] || unit || '';
      return `${value} ${unitStr}`.trim();
    }

    /* Live countdown loop. Ticks every 1s; updates in-place text only
       unless an order transitions to WINDOW_EXPIRED, in which case we
       re-render that row + drop selection if it was the expired one. */
    startCountdownTicker() {
      if (this.state.countdownInterval) clearInterval(this.state.countdownInterval);
      const win = this.state.eligibility?.windowMs;
      if (!win || win <= 0) return; // no window → no countdown
      const tick = () => this.tickCountdowns();
      this.state.countdownInterval = setInterval(tick, 1000);
      tick();
    }

    tickCountdowns() {
      if (this.state.isSpinning) return;
      const now = Date.now();
      const expiredIds = new Set();

      for (const order of this.state.orders) {
        const deadline = this.getOrderDeadline(order);
        if (deadline == null) continue;
        const remaining = deadline - now;
        if (remaining <= 0) {
          expiredIds.add(String(order.id));
          continue;
        }
        const el = this.ordersEl.querySelector(`[data-order-countdown="${order.id}"]`);
        if (el) el.textContent = `⏱ ${this.formatCountdown(remaining)}`;
      }

      if (expiredIds.size === 0) return;

      // Drop expired orders locally for instant feedback. Backend zal ze
      // sowieso wegfilteren bij de volgende /config call, maar zonder
      // de lokale splice blijft de UI tot dan een verlopen order tonen.
      this.state.orders = this.state.orders.filter(o => !expiredIds.has(String(o.id)));
      if (this.state.selectedOrderId && expiredIds.has(this.state.selectedOrderId)) {
        this.state.selectedOrderId = null;
        this.spinBtn.disabled = true;
      }
      this.renderOrders();
      this.setSpinIdleLabel();

      // Sync met portal (geeft verse emptyStateMessage als lijst leeg wordt).
      // Throttle: bundel meerdere expiries in dezelfde tick tot één refresh.
      if (!this._expiryRefreshScheduled) {
        this._expiryRefreshScheduled = true;
        setTimeout(() => {
          this._expiryRefreshScheduled = false;
          this.loadRemoteConfig();
        }, 250);
      }
    }

    renderOrdersError(err) {
      console.error('[golden-wheel] orders fetch failed:', err);
      this.ordersEl.innerHTML = `
        <div class="golden-wheel__orders-state">
          <span>Er ging iets mis bij het laden van je bestellingen. Ververs de pagina.</span>
        </div>`;
    }

    selectOrder(orderId) {
      const order = this.state.orders.find(o => String(o.id) === String(orderId));
      if (!order) return;

      this.state.selectedOrderId = String(orderId);
      this.spinBtn.disabled = false;
      this.spinLabel.textContent = `Draai voor ${order.name}`;

      // Pool-switch: standard vs premium hangt af van de order's flag.
      // applyPool is een no-op als de pool al actief is, dus orders binnen
      // dezelfde pool veroorzaken geen onnodige re-render.
      const targetPool = order.riggedToGoldenTicket ? 'premium' : 'standard';
      this.applyPool(targetPool);

      // Banner + stage-glow worden door state.goldMode gedreven (zelfde
      // signaal als de pool, maar gescheiden zodat visuele flair los van
      // de wheel-render kan worden getuned).
      const wasRigged = !!this.state.goldMode;
      this.state.goldMode = !!order.riggedToGoldenTicket;
      if (wasRigged !== this.state.goldMode) {
        this.renderRiggedBanner();
        this.stage?.classList.toggle('is-rigged', this.state.goldMode);
      }

      this.ordersEl.querySelectorAll('.golden-wheel__order').forEach(el => {
        el.classList.toggle('is-active', el.dataset.orderId === String(orderId));
      });
    }

    /* Show/hide the gold-gradient banner above the wheel based on
       state.goldMode. Banner zit in de Liquid section, JS togglet
       alleen `hidden`. */
    renderRiggedBanner() {
      const banner = this.root.querySelector('[data-wheel-rigged-banner]');
      if (!banner) return;
      banner.hidden = !this.state.goldMode;
    }

    /* Support ?order=xxx in URL (from email / thank-you teaser) */
    handleDeepLink() {
      const params = new URLSearchParams(window.location.search);
      const targetOrder = params.get('order');
      if (!targetOrder) return;
      // Poll until orders load, then auto-select
      const tryAttach = () => {
        const match = this.state.orders.find(o =>
          o.id === targetOrder || o.name === targetOrder || o.name === '#' + targetOrder
        );
        if (match) {
          this.selectOrder(match.id);
          setTimeout(() => this.stage.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        } else if (this.state.orders.length === 0) {
          setTimeout(tryAttach, 200);
        }
      };
      tryAttach();
    }

    /* ---------------------------------------------------------------------
       Spin
       ------------------------------------------------------------------ */

    async spin() {
      if (this.state.isSpinning || !this.state.selectedOrderId) return;
      this.state.isSpinning = true;
      this.stage.classList.add('is-spinning', 'is-zoomed');
      this.spinBtn.disabled = true;

      // Pre-flight: re-fetch /config so we spin against the freshest state.
      // Catches: admin just deleted/added a spin row in Supabase, another tab
      // just consumed this order, jackpot just depleted.
      const stillEligible = await this.verifyEligibility(this.state.selectedOrderId);
      if (!stillEligible) {
        this.abortSpin('Deze order is niet meer beschikbaar om voor te draaien.');
        return;
      }

      let result;
      try {
        result = await this.proxyCall('/spin', { orderId: this.state.selectedOrderId });
      } catch (err) {
        // Error body from portal: { success: false, error: CODE, windowValue?, windowUnit? }
        const code = err.body?.error;
        if (code) {
          this.handleSpinError(code, err.body);
          return;
        }
        console.error('[golden-wheel] spin network failed:', err);
        this.abortSpin('Er ging iets mis met de verbinding. Probeer het opnieuw.');
        return;
      }

      // Defensive: portal returned 200 but with success: false
      if (!result || result.success === false) {
        this.handleSpinError(result?.error, result);
        return;
      }

      // Apply updatedPrizes so other UI state stays fresh
      if (Array.isArray(result.updatedPrizes)) {
        const byId = new Map(result.updatedPrizes.map(p => [p.id, p]));
        let needsRedraw = false;
        for (const seg of this.config.segments) {
          const upd = byId.get(seg.prizeId);
          if (upd) {
            seg.available = upd.available !== false;
            seg.remaining = upd.remaining;
            if (upd.color) seg.color = upd.color;
            needsRedraw = true;
          }
        }
        if (needsRedraw) {
          this.segments = this.generateSegments();
        }
      }

      // Sanity-sync to the BEFORE count silently if our local state was
      // stale (e.g. another tab drained tickets). Never pulses — this is
      // a catch-up, not a win.
      if (result.customerTicketsBefore != null
          && this.state.tickets !== result.customerTicketsBefore) {
        this.setTickets(result.customerTicketsBefore, { silent: true });
      }

      // ❌ Do NOT apply customerTickets here — the wheel still has 3-5
      //    seconds of animation left. Pre-landing reveals are the bug.
      // ✅ Defer until after animateSpin so the +1 puls fires together
      //    with the prize landing under the pointer.

      await this.animateSpin(result);

      // ✅ Wheel has landed — only update the counter if a ticket was
      //    actually awarded (customerTicketsDelta > 0). Pure prize wins
      //    leave the counter untouched (no silent jiggle, no pulse).
      const delta = typeof result.customerTicketsDelta === 'number'
        ? result.customerTicketsDelta
        : null;
      const ticketsAfter = result.customerTickets
        ?? result.tickets
        ?? result.ticketTotal
        ?? result.ticketCount;
      if (delta == null || delta > 0) {
        if (typeof ticketsAfter === 'number') this.setTickets(ticketsAfter);
      }

      const order = this.state.orders.find(o => String(o.id) === String(this.state.selectedOrderId));

      // Single-spin model: na een succesvolle spin is de order altijd op.
      // Geen bonus-chain meer, geen retry op dezelfde order.
      this.showReveal(result, order?.name);
      this.markOrderSpent(this.state.selectedOrderId, result.prize?.label);

      // Refetch history zodat de net-gewonnen prijs bovenaan "Eerder
      // gedraaid" verschijnt. Backend is canonical voor spinId/spunAt.
      if (this.state.history.initialLoadDone) {
        this.fetchHistory();
      }
    }

    abortSpin(message) {
      this.state.isSpinning = false;
      this.stage.classList.remove('is-spinning', 'is-zoomed');
      this.spinBtn.disabled = false;
      if (message) alert(message);
    }

    /* Unified spin-error handler. Message + per-code side effects
       (refresh config, deselect, redirect to login, etc.). */
    handleSpinError(code, body) {
      this.abortSpin(this.errorMessage(code, body));

      switch (code) {
        case 'ALREADY_SPUN':
          this.markOrderSpent(this.state.selectedOrderId);
          this.loadRemoteConfig();
          break;
        case 'WINDOW_EXPIRED':
          // Server noticed the window lapsed between config-load and click.
          // Refresh so that order hops to WINDOW_EXPIRED row state.
          this.loadRemoteConfig();
          break;
        case 'ORDER_NOT_FOUND':
        case 'ORDER_NOT_OWNED':
        case 'ORDER_NOT_ELIGIBLE':
        case 'EVENT_INACTIVE':
          this.loadRemoteConfig();
          break;
        case 'NOT_LOGGED_IN': {
          const ret = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/account/login?return_url=${ret}`;
          break;
        }
      }
    }

    errorMessage(code, body) {
      switch (code) {
        case 'ALREADY_SPUN':
          return 'Voor deze bestelling is al gedraaid.';
        case 'WINDOW_EXPIRED': {
          const win = (body && body.windowValue && body.windowUnit)
            ? this.formatWindow(body.windowValue, body.windowUnit)
            : (this.state.eligibility
              ? this.formatWindow(this.state.eligibility.windowValue, this.state.eligibility.windowUnit)
              : null);
          return win
            ? `Helaas, je had ${win} de tijd na je bestelling om te draaien. Die tijd is nu voorbij.`
            : 'Het tijdsvenster om te draaien voor deze bestelling is voorbij.';
        }
        case 'ORDER_ID_REQUIRED':
          return 'Selecteer eerst een bestelling.';
        case 'ORDER_NOT_ELIGIBLE':
        case 'ORDER_NOT_OWNED':
          return 'Met deze bestelling kun je niet meer draaien.';
        case 'ORDER_NOT_FOUND':
          return 'We kunnen deze bestelling niet vinden. Ververs de pagina en probeer het opnieuw.';
        case 'EVENT_INACTIVE':
          return 'Het Gouden Rad is op dit moment niet actief.';
        case 'NOT_LOGGED_IN':
          return 'Je sessie is verlopen. Log opnieuw in.';
        case 'INTERNAL_ERROR':
          return 'Er ging iets mis aan onze kant. Probeer het over een minuut opnieuw.';
        case 'INVALID_SIGNATURE':
          return 'Er ging iets mis met de beveiliging. Ververs de pagina en probeer het opnieuw.';
        default:
          return 'Er ging iets mis. Probeer het opnieuw.';
      }
    }

    /* Full animation loop — sets rotation + zoom per frame until done. */
    animateSpin(result) {
      return new Promise(resolve => {
        const winningPrizeId = result.prize?.id || result.winningPrizeId;

        // A prize can occupy multiple stripes around the wheel; pick a
        // random one so consecutive wins don't always land on the same
        // visual position.
        const candidates = this.segments.filter(s => s.prizeId === winningPrizeId);
        const seg = candidates.length
          ? candidates[Math.floor(Math.random() * candidates.length)]
          : this.segments[0];

        // Small random jitter inside the wedge so we don't always land dead-center
        const jitter = seg ? (Math.random() - 0.5) * seg.size * 0.55 : 0;
        const landingAngle = seg ? seg.midAngle + jitter : 0;

        // We want the landing angle to sit at the pointer (-PI/2) after rotation.
        // rotation + landingAngle ≡ -PI/2  (mod TAU)
        const targetRotation = -Math.PI / 2 - landingAngle + EXTRA_ROTATIONS * TAU;

        const startTime = performance.now();

        const frame = (now) => {
          const elapsed = now - startTime;
          const t = clamp(elapsed / SPIN_DURATION_MS, 0, 1);

          const rotProgress = easeSpinRotation(t);
          this.state.rotation = targetRotation * rotProgress;
          this.state.zoom = easeZoom(t);

          this.draw();

          if (t < 1) {
            requestAnimationFrame(frame);
          } else {
            this.state.rotation = targetRotation;
            this.draw();
            resolve();
          }
        };
        requestAnimationFrame(frame);
      });
    }

    /* ---------------------------------------------------------------------
       Drawing
       ------------------------------------------------------------------ */

    draw() {
      if (!this.ctx || !this.segments) return;
      const ctx = this.ctx;
      const size = this.size;
      const zoom = this.state.zoom;
      const rotation = this.state.rotation;

      ctx.clearRect(0, 0, size, size);
      ctx.save();

      // Pointer Y target — top of the wheel stays anchored to pointer
      const centerX = size / 2;
      const centerY = POINTER_TOP_INSET + this.radius * zoom;

      ctx.translate(centerX, centerY);
      ctx.scale(zoom, zoom);

      // --- Outer ambient halo (static, doesn't rotate) ---
      this.drawOuterHalo(ctx);

      // --- Wedges + spokes (rotates with wheel; supports fade-in alpha) ---
      const wedgeAlpha = this.state.wedgeOpacity ?? 1;
      if (this.segments.length && wedgeAlpha > 0.001) {
        ctx.save();
        ctx.rotate(rotation);
        if (wedgeAlpha < 1) ctx.globalAlpha = wedgeAlpha;
        for (const seg of this.segments) this.drawWedge(ctx, seg);
        this.drawSpokes(ctx);
        ctx.restore();
      }

      // --- Outer gold rim + rivets (static frame around the spinning wedges) ---
      this.drawRim(ctx, zoom);

      // --- Hub with CN logo (static, on top) ---
      this.drawHub(ctx, zoom);

      ctx.restore();
    }

    /* Ambient glow ring just outside the wheel */
    drawOuterHalo(ctx) {
      const r = this.radius;
      const haloR = r * 1.08;
      const grad = ctx.createRadialGradient(0, 0, r * 0.92, 0, 0, haloR);
      grad.addColorStop(0, 'rgba(255, 202, 112, 0.00)');
      grad.addColorStop(0.55, 'rgba(255, 202, 112, 0.18)');
      grad.addColorStop(1, 'rgba(255, 202, 112, 0.00)');
      ctx.beginPath();
      ctx.arc(0, 0, haloR, 0, TAU);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    /* One wedge = one prize. Fill gradient derived from prize.color,
       with availability + rarity treatments. */
    drawWedge(ctx, seg) {
      const r = this.radius;
      const innerR = r * 0.20;

      // Wedge path
      ctx.beginPath();
      ctx.moveTo(Math.cos(seg.startAngle) * innerR, Math.sin(seg.startAngle) * innerR);
      ctx.lineTo(Math.cos(seg.startAngle) * r, Math.sin(seg.startAngle) * r);
      ctx.arc(0, 0, r, seg.startAngle, seg.endAngle);
      ctx.lineTo(Math.cos(seg.endAngle) * innerR, Math.sin(seg.endAngle) * innerR);
      ctx.arc(0, 0, innerR, seg.endAngle, seg.startAngle, true);
      ctx.closePath();

      // Fill: portal owns the colour palette so we render `prize.color`
      // directly with a subtle inner→outer shade for depth. Golden Ticket
      // gets a special radial gradient per spec.
      ctx.fillStyle = this.getWedgeFill(ctx, seg, innerR, r);
      ctx.fill();

      // Unavailable: heavy dark overlay + diagonal hatching feel
      if (!seg.available) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.fill();
      }

      // Rarity glow overlay — animated pulse for rare+ tiers.
      // Each rarity gets its own base alpha; pulse is time-based so the
      // wheel "breathes" even when idle, drawing the eye to rare wins.
      if (seg.available && seg.rarity !== 'common') {
        const t = performance.now() / 1000;
        // Phase-offset the pulse by midAngle so adjacent rares don't pulse in sync
        const pulse = 0.55 + 0.45 * Math.sin(t * 2.4 + seg.midAngle * 1.6);
        const baseAlpha = seg.rarity === 'legendary' ? 0.85
                        : seg.rarity === 'epic'      ? 0.65
                                                     : 0.45;  // rare
        const alpha = baseAlpha * pulse;

        const midR = (innerR + r) * 0.58;
        const gx = Math.cos(seg.midAngle) * midR;
        const gy = Math.sin(seg.midAngle) * midR;
        const glow = ctx.createRadialGradient(gx, gy, 2, gx, gy, r * 0.48);
        glow.addColorStop(0, `rgba(255, 240, 160, ${alpha.toFixed(3)})`);
        glow.addColorStop(0.6, `rgba(255, 202, 112, ${(alpha * 0.35).toFixed(3)})`);
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fill();

        // Legendary gets an additional bright sparkle ring at the outer edge
        if (seg.rarity === 'legendary') {
          const sparkleAlpha = 0.4 + 0.6 * pulse;
          ctx.save();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = `rgba(255, 255, 210, ${sparkleAlpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(0, 0, r - 6, seg.startAngle + 0.02, seg.endAngle - 0.02);
          ctx.stroke();
          ctx.restore();
        }
      }

      // Golden Ticket wedges NEVER render a product image, even if one
      // happens to be attached upstream — the radial gold gradient is the
      // entire visual. Defense-in-depth against stale state or upstream
      // schema drift where a golden_ticket prize carries an icon URL.
      const isGoldenTicket = seg.type === 'golden_ticket';

      // Teaser mode (customer has no eligible orders): ALL non-golden_ticket
      // wedges render as "?" placeholders on a gold background. The
      // golden_ticket wedges keep their label so the teaser still shows
      // the hook — qualify for an order and you can win these.
      const isTeaserTile = this.state.teaserMode && !isGoldenTicket;

      // When the customer has no eligible order, we hide product info
      // for shopify_variant prizes — replace image + label with a "?"
      // placeholder. Teaser mode expands this to ALL non-golden_ticket
      // types (custom, discount, etc.) so the entire prize-pool stays
      // a mystery until the customer qualifies.
      const isVariant = seg.type === 'shopify_variant';
      const hideProductInfo = isTeaserTile
        || (isVariant && !this.state.canSeeProducts);

      // Product image (if available) — sits near the outer rim of the wedge.
      // Only if the wedge is wide enough to hold it legibly.
      const img = seg.image ? this.imageCache?.get(seg.image) : null;
      const imgReady = img && img.complete && img.naturalWidth > 0;
      const imgMidR = innerR + (r - innerR) * 0.66;
      const arcAtMid = seg.size * imgMidR;
      const hasImage = imgReady && arcAtMid > 22 && !hideProductInfo && !isGoldenTicket;

      if (hasImage) {
        this.drawWedgeImage(ctx, seg, img, innerR, r);
      } else if (hideProductInfo && arcAtMid > 22) {
        // "?" placeholder where the product image would go
        this.drawWedgePlaceholder(ctx, seg, innerR, r);
      } else if (isGoldenTicket) {
        // Pure Golden Ticket: rond bolletje met 🎫 icoon. drawWedgeTicketIcon
        // doet zelf een early-return als de stripe te smal is — dan blijft
        // alleen de gouden radial gradient zichtbaar (no fallback to text).
        this.drawWedgeTicketIcon(ctx, seg, innerR, r);
      } else {
        // No image / placeholder / ticket applicable — show label
        this.drawWedgeLabel(ctx, seg, innerR, r, false);
      }

      // Rigged-spin bonus indicator: kleine 🎫 badge bovenop non-ticket
      // wedges om visueel te communiceren dat je hier OOK een Gouden Lot
      // krijgt. Hidden info bij teaser/canSee placeholders — we willen
      // de prize-pool dan niet alsnog leak'en via "wat krijg je erbij".
      if (this.state.goldMode && !isGoldenTicket && !hideProductInfo
          && arcAtMid > 28) {
        this.drawWedgeBonusBadge(ctx, seg, innerR, r);
      }
    }

    /* Premium ticket chip for golden_ticket wedges — gebruikt dezelfde
       gold-ring-on-dark + light-inner structuur als drawWedgeImage, met
       een 🎫 emoji ipv productfoto in het midden. Visueel coherent met
       de andere prize-tiles op het rad, en het emoji-icoon valt niet
       meer weg tegen de gouden wedge-gradient.

       Elke stripe van een multi-segment Golden Ticket prize krijgt zijn
       eigen chip op de angular midpoint. Counter-rotates met de wheel
       zodat de chip altijd rechtop staat. */
    drawWedgeTicketIcon(ctx, seg, innerR, outerR) {
      // Match drawWedgeImage's positioning + sizing exactly zodat de
      // chip dezelfde dimensies krijgt als product-cirkels — visual
      // coherence over alle wedge-types.
      const midR = innerR + (outerR - innerR) * 0.66;
      const x = Math.cos(seg.midAngle) * midR;
      const y = Math.sin(seg.midAngle) * midR;

      const arcWidth = seg.size * midR;
      const radialBudget = (outerR - midR) * 2 - 10;
      // Too narrow → laat de gouden gradient z'n werk doen, geen chip.
      if (arcWidth < 18 || radialBudget < 18) return;
      const size = clamp(Math.min(arcWidth * 0.85, radialBudget), 30, 92);
      const ringPad = 2;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-this.state.rotation);

      this.drawTicketCoin(ctx, size, ringPad);

      ctx.restore();
    }

    /* Gouden ticket-shape getekend op canvas, op een dark coin disc.
       Centered op (0,0). Gebruikt door drawWedgeTicketIcon (wheel tile)
       en drawWedgeBonusBadge (kleine sticker). Eén implementatie zodat
       alle ticket-visualizations op het rad consistent zijn én max
       contrast hebben met de gouden cake-wedges eromheen. */
    drawTicketCoin(ctx, size, ringPad = 2) {
      // 1) Outer gold halo glow — coin pop't hierdoor uit de gold-cake.
      ctx.save();
      ctx.shadowColor = 'rgba(255, 210, 120, 0.85)';
      ctx.shadowBlur = Math.max(6, size * 0.18);
      ctx.beginPath();
      ctx.arc(0, 0, size / 2 + ringPad + 1, 0, TAU);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.96)';
      ctx.fill();
      ctx.restore();

      // 2) Bright gold ring — dikker dan product-circles voor "premium coin"
      ctx.beginPath();
      ctx.arc(0, 0, size / 2 + ringPad, 0, TAU);
      ctx.lineWidth = Math.max(1.6, size * 0.04);
      ctx.strokeStyle = '#ffd66f';
      ctx.stroke();

      // 3) Inner dark disc (charcoal radial)
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, TAU);
      ctx.closePath();
      ctx.clip();

      const innerGrad = ctx.createRadialGradient(0, 0, size * 0.08, 0, 0, size * 0.55);
      innerGrad.addColorStop(0, '#1f1a14');
      innerGrad.addColorStop(0.7, '#0d0a07');
      innerGrad.addColorStop(1, '#000000');
      ctx.fillStyle = innerGrad;
      ctx.fillRect(-size / 2, -size / 2, size, size);

      // 4) Subtiele gouden binnenring (hint van diepte aan rand)
      ctx.beginPath();
      ctx.arc(0, 0, size / 2 - 2, 0, TAU);
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = 'rgba(255, 210, 120, 0.4)';
      ctx.stroke();

      // 5) Gouden ticket-shape in het centrum: rounded rect met
      //    semicircle-notches op links + rechts midpoints, + dashed
      //    perforatie. Ipv emoji (was OS-rendered + dark on dark).
      this.drawTicketShape(ctx, size);

      ctx.restore();
    }

    /* Gouden ticket-pad op canvas — rounded rect met notches. Centered
       op (0,0), past in een vierkant van `size`. Wordt ge-clipped door
       drawTicketCoin's circle clip, dus mag iets groter dan disc. */
    drawTicketShape(ctx, size) {
      const tw = size * 0.78;
      const th = size * 0.46;
      const cr = th * 0.20;
      const nr = th * 0.22;

      ctx.beginPath();
      ctx.moveTo(-tw / 2 + cr, -th / 2);
      ctx.lineTo(tw / 2 - cr, -th / 2);
      ctx.arcTo(tw / 2, -th / 2, tw / 2, -th / 2 + cr, cr);
      ctx.lineTo(tw / 2, -nr);
      ctx.arc(tw / 2, 0, nr, -Math.PI / 2, Math.PI / 2, true);
      ctx.lineTo(tw / 2, th / 2 - cr);
      ctx.arcTo(tw / 2, th / 2, tw / 2 - cr, th / 2, cr);
      ctx.lineTo(-tw / 2 + cr, th / 2);
      ctx.arcTo(-tw / 2, th / 2, -tw / 2, th / 2 - cr, cr);
      ctx.lineTo(-tw / 2, nr);
      ctx.arc(-tw / 2, 0, nr, Math.PI / 2, -Math.PI / 2, true);
      ctx.lineTo(-tw / 2, -th / 2 + cr);
      ctx.arcTo(-tw / 2, -th / 2, -tw / 2 + cr, -th / 2, cr);
      ctx.closePath();

      const grad = ctx.createLinearGradient(-tw / 2, -th / 2, tw / 2, th / 2);
      grad.addColorStop(0, '#9a722e');
      grad.addColorStop(0.5, '#ffd680');
      grad.addColorStop(1, '#9a722e');
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.lineWidth = Math.max(0.6, size * 0.018);
      ctx.strokeStyle = '#2a1f10';
      ctx.stroke();

      // Dashed perforatie — alleen bij groter formaat zichtbaar
      if (size >= 28) {
        ctx.save();
        ctx.setLineDash([Math.max(1, size * 0.04), Math.max(1, size * 0.04)]);
        ctx.lineWidth = Math.max(0.5, size * 0.012);
        ctx.strokeStyle = 'rgba(42, 31, 16, 0.7)';
        ctx.beginPath();
        ctx.moveTo(0, -th / 2 + cr * 0.6);
        ctx.lineTo(0, th / 2 - cr * 0.6);
        ctx.stroke();
        ctx.restore();
      }
    }

    /* Small bonus 🎫 badge — toont in rigged-state op alle non-ticket
       wedges om te communiceren dat de klant hier OOK een Gouden Lot
       wint bovenop de prijs. Zit boven-rechts ten opzichte van de
       product-cirkel, in een gouden bol met dunne donkere rand. */
    drawWedgeBonusBadge(ctx, seg, innerR, outerR) {
      const midR = innerR + (outerR - innerR) * 0.66;
      const x = Math.cos(seg.midAngle) * midR;
      const y = Math.sin(seg.midAngle) * midR;

      const arcWidth = seg.size * midR;
      const radialBudget = (outerR - midR) * 2 - 10;
      if (arcWidth < 22 || radialBudget < 22) return;

      // Product-cirkel grootte = clamp(arc*0.85, 30, 92). Badge = ~38%
      // daarvan (12-32px). Niet te groot — moet "notification dot" vibe
      // houden, niet de eigenlijke prize overschaduwen.
      const productSize = clamp(Math.min(arcWidth * 0.85, radialBudget), 30, 92);
      const badgeSize = clamp(productSize * 0.38, 18, 34);

      // Positioneer boven-rechts van het center, in screen-space ná
      // counter-rotate. Lichte overlap met product-cirkel rand zodat
      // het echt als sticker aanvoelt.
      const offset = productSize * 0.36;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-this.state.rotation);
      ctx.translate(offset, -offset);

      // Dark coin + gouden ticket — was eerst cream/gold disc met emoji,
      // maar dat verdween tegen de gouden wedge én tegen het cream-gold
      // van de ticket-chip naast 'm. Nu zelfde stijl als de hoofd-tile:
      // dark coin met gold ring + glow + canvas-drawn ticket.
      this.drawTicketCoin(ctx, badgeSize, 1.5);

      ctx.restore();
    }

    /* "?" circle in place of the product image when the customer has no
       eligible order — keeps prize identities hidden until they qualify.
       Scales with wheel radius: small on mobile, generous on desktop.

       The disc is drawn INSIDE the wheel-rotated canvas frame, so we
       counter-rotate by `state.rotation` to keep the "?" glyph pointing
       upright in screen space at all times (idle drift / spin animation
       would otherwise tilt every placeholder along with the wheel). */
    drawWedgePlaceholder(ctx, seg, innerR, outerR) {
      const midR = innerR + (outerR - innerR) * 0.66;
      const x = Math.cos(seg.midAngle) * midR;
      const y = Math.sin(seg.midAngle) * midR;

      const arcWidth = seg.size * midR;
      const radialBudget = (outerR - midR) * 2 - 14;
      // Mobile (outerR ~140): max ~56. Desktop (outerR ~320): max ~100.
      const maxSize = clamp(outerR * 0.32, 56, 100);
      const size = clamp(Math.min(arcWidth * 0.6, radialBudget), 24, maxSize);

      ctx.save();
      ctx.translate(x, y);
      // Cancel out the wheel's rotation so the "?" always points up.
      ctx.rotate(-this.state.rotation);

      // Dim disc + thin gold ring — sits on a black wedge in teaser mode,
      // the semi-transparent fill darkens slightly relative to the slice
      // so the "?" chip has subtle depth without standing apart.
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, TAU);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255, 230, 138, 0.7)';
      ctx.stroke();

      // "?" mark — large enough to dominate the disc, no leftover dead
      // space around it.
      ctx.fillStyle = 'rgba(255, 230, 138, 0.95)';
      ctx.font = `800 ${Math.round(size * 0.85)}px 'Commuters Sans', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(255, 202, 112, 0.5)';
      ctx.shadowBlur = 4;
      ctx.fillText('?', 0, 1);

      ctx.restore();
    }

    /* Build the fill for a wedge.
       - golden_ticket → radial gradient #FBD68C → #D7B865 → #9B7232 (per spec).
       - teaser-mode non-golden → solid black slice. Hides prize identity AND
         contrasts with the gold Golden Ticket wedges so the eye snaps to those.
       - everything else → use `seg.color` from portal verbatim, with a
         subtle inner-darken / outer-brighten for depth, desaturated when
         the prize is no longer available. */
    getWedgeFill(ctx, seg, innerR, outerR) {
      if (seg.type === 'golden_ticket') {
        const cx = Math.cos(seg.midAngle) * (innerR + outerR) * 0.5;
        const cy = Math.sin(seg.midAngle) * (innerR + outerR) * 0.5;
        const grad = ctx.createRadialGradient(
          cx, cy, Math.max(2, innerR * 0.3),
          cx, cy, outerR * 0.7
        );
        grad.addColorStop(0, '#FBD68C');
        grad.addColorStop(0.5, '#D7B865');
        grad.addColorStop(1, '#9B7232');
        return grad;
      }

      // Teaser-mode: non-golden wedges are masked → flat dark slice with
      // subtle inner→outer shade so it still reads as a 3D segment.
      if (this.state.teaserMode) {
        const grad = ctx.createLinearGradient(
          Math.cos(seg.midAngle) * innerR, Math.sin(seg.midAngle) * innerR,
          Math.cos(seg.midAngle) * outerR, Math.sin(seg.midAngle) * outerR
        );
        grad.addColorStop(0, '#050403');
        grad.addColorStop(1, '#15100c');
        return grad;
      }

      const baseRgb = hexToRgb(seg.color);
      const rgb = seg.available ? baseRgb : desaturateRgb(baseRgb, 0.9);
      const innerShade = shadeRgb(rgb, seg.available ? -25 : -55);
      const outerShade = shadeRgb(rgb, seg.available ? 10 : -20);

      const gx0 = Math.cos(seg.midAngle) * innerR;
      const gy0 = Math.sin(seg.midAngle) * innerR;
      const gx1 = Math.cos(seg.midAngle) * outerR;
      const gy1 = Math.sin(seg.midAngle) * outerR;
      const grad = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
      grad.addColorStop(0, rgbCss(innerShade));
      grad.addColorStop(1, rgbCss(outerShade));
      return grad;
    }

    drawWedgeImage(ctx, seg, img, innerR, outerR) {
      // Position toward the outer rim — leaves the inner half clear and
      // makes products feel like prizes "on" the wheel rather than
      // floating in the middle.
      const midR = innerR + (outerR - innerR) * 0.66;
      const x = Math.cos(seg.midAngle) * midR;
      const y = Math.sin(seg.midAngle) * midR;

      // Size: limited by the wedge's arc width and by radial space
      // remaining toward the rim (so the gold ring never clips the rim).
      const arcWidth = seg.size * midR;
      const radialBudget = (outerR - midR) * 2 - 10;
      const size = clamp(Math.min(arcWidth * 0.85, radialBudget), 30, 92);
      const ringPad = 2;

      ctx.save();
      ctx.translate(x, y);

      // Gold ring + dark backing behind image
      ctx.beginPath();
      ctx.arc(0, 0, size / 2 + ringPad, 0, TAU);
      ctx.fillStyle = 'rgba(10, 7, 5, 0.85)';
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = seg.rarity === 'common'
        ? 'rgba(255, 230, 138, 0.55)'
        : 'rgba(255, 230, 138, 0.9)';
      ctx.stroke();

      // Clip to a circle, then drawImage with contain-fit
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, TAU);
      ctx.closePath();
      ctx.clip();

      // Soft white underlay so translucent PNGs stay visible on dark bg
      ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.fillRect(-size / 2, -size / 2, size, size);

      const aspect = img.naturalWidth / img.naturalHeight;
      let dw = size, dh = size;
      if (aspect > 1) dh = size / aspect;
      else dw = size * aspect;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);

      ctx.restore();
      ctx.restore();
    }

    /* Rotate to the wedge's mid-angle, draw text reading outward → inward.
       When the wedge has a product image, text is limited to the outer
       half (between image and rim) so the two don't collide. */
    drawWedgeLabel(ctx, seg, innerR, outerR, hasImage = false) {
      if (!seg.label) return;

      const availAngle = seg.size;
      const availRadial = outerR - innerR;
      // Slightly smaller multiplier than before so longer words like
      // "GOLDEN TICKET" fit on smaller (mobile) wheels without truncating.
      let fontSize = clamp(outerR * 0.05, 9, 18);
      const arcChordAtMidR = 2 * Math.sin(availAngle / 2) * ((innerR + outerR) / 2);
      if (arcChordAtMidR < fontSize * 0.95) fontSize = Math.max(8, fontSize * 0.75);
      if (arcChordAtMidR < 9) return; // too thin to render legibly

      ctx.save();
      ctx.rotate(seg.midAngle);
      ctx.textBaseline = 'middle';
      ctx.font = `700 ${fontSize}px 'Commuters Sans', sans-serif`;
      ctx.fillStyle = seg.available ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.35)';

      // Available radial band for the label. With an image present the
      // label sits in the outer half above it; standalone, it spans the
      // whole wedge. Either way, we center the text along the band so
      // the label sits visually balanced inside its wedge.
      const bandStart = hasImage
        ? innerR + availRadial * 0.62
        : innerR + availRadial * 0.06;
      const bandEnd = outerR - availRadial * 0.06;
      const textCenter = (bandStart + bandEnd) / 2;
      const maxWidth = bandEnd - bandStart;

      // Keep the leading 🎫 on Golden Ticket labels as a subtle accent.
      const label = (seg.label || '').toUpperCase();
      const truncated = truncateToWidth(ctx, label, maxWidth);

      ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;
      ctx.textAlign = 'center';
      ctx.fillText(truncated, textCenter, 0);

      if (!seg.available) {
        const labelWidth = ctx.measureText(truncated).width;
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(255, 180, 180, 0.85)';
        ctx.font = `700 ${Math.max(8, fontSize * 0.55)}px 'Commuters Sans', sans-serif`;
        ctx.textAlign = 'right';
        ctx.fillText('✕ OP', textCenter - labelWidth / 2 - 6, 0);
      }
      ctx.restore();
    }

    /* Thin gold spokes between wedges — subtle depth */
    drawSpokes(ctx) {
      const r = this.radius;
      const innerR = r * 0.20;
      ctx.save();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255, 230, 138, 0.28)';
      for (const seg of this.segments) {
        const a = seg.startAngle;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * innerR, Math.sin(a) * innerR);
        ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        ctx.stroke();
      }
      ctx.restore();
    }

    /* Outer gold rim frame — thick gradient ring with highlight + outer glow.
       Doesn't rotate; it's the chrome around the spinning wedges. */
    drawRim(ctx, zoom) {
      const r = this.radius;
      ctx.save();

      // Thick gold rim (outer ring) — palette matches button/teaser wheel.
      const rimW = Math.max(4, r * 0.028);
      const rimGrad = ctx.createLinearGradient(-r, -r, r, r);
      rimGrad.addColorStop(0.00, '#ffe1a0');
      rimGrad.addColorStop(0.25, '#ffca70');
      rimGrad.addColorStop(0.50, '#9a722e');
      rimGrad.addColorStop(0.75, '#ffca70');
      rimGrad.addColorStop(1.00, '#ffe1a0');
      ctx.lineWidth = rimW;
      ctx.strokeStyle = rimGrad;
      ctx.beginPath();
      ctx.arc(0, 0, r - rimW / 2 + 0.5, 0, TAU);
      ctx.stroke();

      // Inner bright hairline just inside the rim
      ctx.lineWidth = Math.max(0.8, 1 / zoom);
      ctx.strokeStyle = 'rgba(255, 225, 160, 0.6)';
      ctx.beginPath();
      ctx.arc(0, 0, r - rimW - 0.5, 0, TAU);
      ctx.stroke();

      // Outer soft glow line
      ctx.lineWidth = Math.max(1, 1.5 / zoom);
      ctx.strokeStyle = 'rgba(255, 202, 112, 0.4)';
      ctx.beginPath();
      ctx.arc(0, 0, r + 1.5, 0, TAU);
      ctx.stroke();

      // Rivets / dots around the rim — one per segment boundary, plus mid points
      const segCount = this.segments.length;
      if (segCount > 0 && segCount <= 40) {
        const dotR = Math.max(1.1, r * 0.008);
        const dotPlacementR = r - rimW / 2 + 0.5;
        // Rivets are part of the static rim, but we want them aligned with segment
        // boundaries — so draw in the ROTATED frame. Re-apply rotation here.
        ctx.save();
        ctx.rotate(this.state.rotation);
        for (const seg of this.segments) {
          const a = seg.startAngle;
          const dx = Math.cos(a) * dotPlacementR;
          const dy = Math.sin(a) * dotPlacementR;
          ctx.fillStyle = '#ffe1a0';
          ctx.beginPath();
          ctx.arc(dx, dy, dotR, 0, TAU);
          ctx.fill();
          // Tiny dark center for "rivet" depth
          ctx.fillStyle = 'rgba(68, 53, 28, 0.9)';
          ctx.beginPath();
          ctx.arc(dx, dy, dotR * 0.45, 0, TAU);
          ctx.fill();
        }
        ctx.restore();
      }

      ctx.restore();
    }

    /* Central hub with CN logo — static (doesn't rotate with wheel) */
    drawHub(ctx, zoom) {
      const r = this.radius;
      const hubR = r * 0.20;
      if (zoom > 3) return; // fully zoomed past the hub; skip render

      // Outer gold ring — palette matches button/teaser wheel
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, hubR, 0, TAU);
      const ringGrad = ctx.createLinearGradient(-hubR, -hubR, hubR, hubR);
      ringGrad.addColorStop(0.00, '#ffe1a0');
      ringGrad.addColorStop(0.50, '#9a722e');
      ringGrad.addColorStop(1.00, '#ffe1a0');
      ctx.lineWidth = Math.max(2, hubR * 0.08);
      ctx.strokeStyle = ringGrad;
      ctx.stroke();

      // Inner black disc with radial highlight (glossy)
      const discR = hubR - Math.max(2, hubR * 0.08) / 2 - 1;
      const discGrad = ctx.createRadialGradient(-discR * 0.35, -discR * 0.4, 2, 0, 0, discR);
      discGrad.addColorStop(0, '#2a1f12');
      discGrad.addColorStop(1, '#050403');
      ctx.beginPath();
      ctx.arc(0, 0, discR, 0, TAU);
      ctx.fillStyle = discGrad;
      ctx.fill();

      // Thin inner hairline
      ctx.lineWidth = Math.max(0.5, 0.6 / zoom);
      ctx.strokeStyle = 'rgba(255, 225, 160, 0.6)';
      ctx.beginPath();
      ctx.arc(0, 0, discR - 2, 0, TAU);
      ctx.stroke();

      // CN logo bitmap centered in the hub
      if (this.logoImg && this.logoImg.complete) {
        const logoW = discR * 1.45;
        const aspect = (this.logoImg.naturalHeight || 2500) / (this.logoImg.naturalWidth || 8000);
        const logoH = logoW * aspect;
        ctx.drawImage(this.logoImg, -logoW / 2, -logoH / 2, logoW, logoH);
      } else {
        // Fallback: gold "CN" text
        ctx.fillStyle = '#ffe1a0';
        ctx.font = `700 ${Math.round(hubR * 0.55)}px 'Commuters Sans', 'Toxigenesis', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('CN', 0, 0);
      }

      ctx.restore();
    }

    /* ---------------------------------------------------------------------
       Reveal modal
       ------------------------------------------------------------------ */

    /* Win-modal heeft drie varianten op basis van de spin-response:
       1) pure golden_ticket  → "Je hebt een Golden Ticket gewonnen!"
       2) combo (prize + 🎫)  → product + "óók een Golden Ticket erbij!"
       3) pure prize          → enkel het gewonnen product

       Combo wordt herkend aan `result.grantsGoldenTicket === true` op
       een prize die zelf géén type=golden_ticket heeft. Pure golden
       tickets impliceren `grantsGoldenTicket` automatisch. */
    showReveal(result, orderName) {
      const titleEl = this.reveal.querySelector('[data-wheel-reveal-title]');
      const eyebrowEl = this.reveal.querySelector('[data-wheel-reveal-rarity]');
      const textEl = this.reveal.querySelector('[data-wheel-reveal-text]');
      const productWrap = this.reveal.querySelector('[data-wheel-reveal-product]');
      const ticketWrap = this.reveal.querySelector('[data-wheel-reveal-ticket]');
      const closeBtn = this.reveal.querySelector('[data-wheel-reveal-close]');

      const prize = result.prize || {
        id: result.winningPrizeId,
        label: result.prizeLabel,
        rarity: null,
        description: result.prizeDescription,
      };

      const variant = prize?.shopifyVariant;
      const isShopifyVariant = prize?.type === 'shopify_variant' && variant;
      const isPureGoldenTicket = prize?.type === 'golden_ticket'
        || /golden.?ticket/i.test(prize?.label || '');
      const bonusPrize = result.bonusPrize || null;
      const hasBonusPrize = !!bonusPrize;

      // Vier modal-varianten:
      //  1) pure ticket (rad landde op ticket, geen bonus)              → ticket-only
      //  2) ticket + product bonus (rigged, rad landde op ticket)        → garantie-spin
      //  3) product + ticket bonus (rigged, rad landde op product)       → combo
      //  4) pure prize (rad landde op product, geen bonus)               → prize-only
      const isTicketOnly = isPureGoldenTicket && !hasBonusPrize;
      const isTicketWithBonus = isPureGoldenTicket && hasBonusPrize;
      const isCombo = !isPureGoldenTicket && !!result.grantsGoldenTicket;

      // Eyebrow + title
      if (isTicketOnly) {
        eyebrowEl.innerHTML = `${ticketSvgInline()} Gouden Lot gewonnen!`;
        titleEl.hidden = true;
      } else if (isTicketWithBonus) {
        // NEW: rad landde visueel op een ticket, server geeft een bonus
        // product erbij (garantie-spin). Eyebrow signaleert dat duidelijk,
        // titel is de ticket-naam zelf.
        eyebrowEl.innerHTML = `${ticketSvgInline()} Garantie-spin gewonnen!`;
        titleEl.hidden = false;
        titleEl.textContent = prize.label || 'Gouden Lot';
      } else {
        eyebrowEl.textContent = 'Gefeliciteerd Groei-Maat!';
        titleEl.hidden = false;
        titleEl.textContent = isShopifyVariant
          ? (variant.productTitle || prize.label || 'Gewonnen!')
          : (prize.label || 'Gewonnen!');
      }

      // Visual area: ticket hero voor élke pure-ticket case (met of zonder
      // bonus — de bonus product komt in de body als card). Product image
      // voor de overige cases (combo + pure prize).
      if (ticketWrap) ticketWrap.hidden = !isPureGoldenTicket;
      this.renderRevealProduct(productWrap, isPureGoldenTicket ? null : prize);

      // Body copy
      if (isTicketWithBonus) {
        this.renderTicketWithBonusBody(textEl, bonusPrize, orderName);
      } else if (isTicketOnly) {
        this.renderGoldenTicketBody(textEl, prize);
      } else if (isCombo) {
        this.renderComboBody(textEl, prize, orderName);
      } else if (isShopifyVariant) {
        textEl.textContent = orderName
          ? `Sturen we direct mee met jouw bestelling (${orderName}).`
          : 'Sturen we direct mee met je volgende bestelling.';
      } else {
        this.renderRevealDescription(textEl, prize.description, false);
      }

      if (closeBtn) closeBtn.textContent = 'Sluiten';

      // Golden ticket flair on the card — combo's krijgen 'm ook (er ZIT
      // immers een ticket bij), zodat de gouden glow ook bij combo's
      // signaleert "ticket gewonnen".
      this.reveal.classList.toggle(
        'is-golden-ticket',
        isPureGoldenTicket || isCombo
      );

      this._previousRevealFocus = document.activeElement;
      this.reveal.hidden = false;
      requestAnimationFrame(() => {
        this.reveal.setAttribute('data-open', '');
        closeBtn?.focus?.();
      });
    }

    /* Combo-body: product wordt toegevoegd aan de order EN er zit een
       Gouden Lot bij. Layout:
         1) "+ 🎫 Gouden Lot" badge — bonus-indicator
         2) "🎫 Meer loten = meer kans" pill — de waarom-uitleg
         3) "Het product sturen we direct mee..." — order-context
       De twee gouden elementen staan visueel boven elkaar zodat het
       gouden-lot-verhaal samen leest, daarna pas de praktische
       leverings-info. */
    renderComboBody(el, prize, orderName) {
      if (!el) return;
      const orderLine = orderName
        ? `Het product sturen we direct mee met jouw bestelling (${orderName}).`
        : 'Het product sturen we direct mee met je volgende bestelling.';
      el.innerHTML = [
        `<span class="golden-wheel__reveal-combo-badge">+ ${ticketSvgInline()} Gouden Lot</span>`,
        `<span class="golden-wheel__reveal-text-pill">${ticketSvgInline()} Meer loten = meer kans</span>`,
        `<span class="golden-wheel__reveal-text-intro">${escapeHtml(orderLine)}</span>`,
      ].join('');
    }

    /* Garantie-spin body: rad landde op een ticket, server geeft een
       bonus product erbij (alleen mogelijk bij riggedSpin === true). De
       bonus product card is de focal point — toont image + label zodat
       de klant direct ziet wat 'ie naast de ticket krijgt. */
    renderTicketWithBonusBody(el, bonusPrize, orderName) {
      if (!el || !bonusPrize) return;

      const bonusLabel = bonusPrize.label || 'Bonus prijs';
      const bonusVariant = bonusPrize.shopifyVariant || null;

      const imageHtml = bonusVariant?.image
        ? `<div class="golden-wheel__reveal-bonus-product-image">
             <img src="${escapeHtml(bonusVariant.image)}" alt="" loading="lazy" />
           </div>`
        : `<div class="golden-wheel__reveal-bonus-product-image golden-wheel__reveal-bonus-product-image--ticket">
             ${ticketSvgInline()}
           </div>`;

      const variantLine = bonusVariant?.variantTitle
        ? `<p class="golden-wheel__reveal-bonus-product-variant">${escapeHtml(bonusVariant.variantTitle)}</p>`
        : '';

      const orderLine = orderName
        ? `Het bonus product sturen we direct mee met jouw bestelling (${orderName}).`
        : 'Het bonus product sturen we direct mee met je volgende bestelling.';

      el.innerHTML = [
        `<div class="golden-wheel__reveal-bonus-product">`,
          imageHtml,
          `<div class="golden-wheel__reveal-bonus-product-info">`,
            `<p class="golden-wheel__reveal-bonus-product-eyebrow">+ Bonus product</p>`,
            `<p class="golden-wheel__reveal-bonus-product-label">${escapeHtml(bonusLabel)}</p>`,
            variantLine,
          `</div>`,
        `</div>`,
        `<span class="golden-wheel__reveal-text-intro">${escapeHtml(orderLine)}</span>`,
        `<span class="golden-wheel__reveal-text-pill">${ticketSvgInline()} Meer loten = meer kans</span>`,
      ].join('');
    }

    /* Render the prize description into the modal text slot, respecting
       newlines from the portal:
         \n\n = paragraph break
         \n   = line break (preserved by white-space: pre-line in CSS) */
    renderRevealDescription(el, description, _isGoldenTicket) {
      if (!el) return;
      // Plain prose for custom / fallback prizes — pre-line CSS handles \n
      el.textContent = (description || '').trim();
    }

    /* Special body for Golden Ticket reveals: hero headline + bulleted
       list of the prizes the ticket entitles them to (parsed from the
       portal's description prose) + a gold "Meer Tickets = Meer Kans"
       pill at the bottom. */
    renderGoldenTicketBody(el, prize) {
      if (!el) return;

      const description = (prize.description || '').trim();
      const headline = (description.split(/\n+/)[0] || '').trim()
        || 'Gefeliciteerd Groei-Maat!';
      const bullets = this.parseGoldenTicketBullets(description);

      const introHtml = bullets.length
        ? `<span class="golden-wheel__reveal-text-intro">Je maakt nu kans op:</span>`
        : '';

      const bulletsHtml = bullets.length
        ? `<ul class="golden-wheel__reveal-text-bullets">${
            bullets.map(b => `<li>${escapeHtml(b)}</li>`).join('')
          }</ul>`
        : '';

      // No whitespace between tags — parent has white-space: pre-line.
      el.innerHTML = [
        `<span class="golden-wheel__reveal-text-headline">${escapeHtml(headline)}</span>`,
        introHtml,
        bulletsHtml,
        `<span class="golden-wheel__reveal-text-pill">${ticketSvgInline()} Meer loten = meer kans</span>`,
      ].join('');
    }

    /* Open the info modal — populated from de live Golden Ticket prize.
       Zoekt EXPLICIET in de standard pool: premium pool heeft per
       definitie geen tickets, dus zonder deze override zou de modal
       leeg blijven als de klant op een premium order kijkt. */
    openInfoModal() {
      if (!this.infoModal) return;
      const body = this.infoModal.querySelector('[data-wheel-info-body]');
      if (body) {
        const ticketPool = this.config.standardSegments
          || this.config.segments
          || [];
        const goldenTicket = ticketPool.find(s =>
          s && s.type === 'golden_ticket'
        );
        const description = (goldenTicket?.description || '').trim();
        const bullets = this.parseGoldenTicketBullets(description);

        const introHtml = bullets.length
          ? `<span class="golden-wheel__reveal-text-intro">Je maakt nu kans op:</span>`
          : '';
        const bulletsHtml = bullets.length
          ? `<ul class="golden-wheel__reveal-text-bullets">${
              bullets.map(b => `<li>${escapeHtml(b)}</li>`).join('')
            }</ul>`
          : '';

        body.innerHTML = [
          `<span class="golden-wheel__reveal-text-headline">Meer loten = meer kans</span>`,
          introHtml,
          bulletsHtml,
          `<span class="golden-wheel__reveal-text-pill">${ticketSvgInline()} Verzamel Gouden Loten</span>`,
        ].join('');
      }

      this._previousFocus = document.activeElement;
      this.infoModal.hidden = false;
      requestAnimationFrame(() => {
        this.infoModal.setAttribute('data-open', '');
        // Move focus into the modal for keyboard users
        this.infoModal.querySelector('[data-wheel-info-close]')?.focus?.();
      });
    }

    closeInfoModal() {
      if (!this.infoModal) return;
      this.infoModal.removeAttribute('data-open');
      setTimeout(() => { this.infoModal.hidden = true; }, 300);
      // Return focus to whatever opened the modal
      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        try { this._previousFocus.focus(); } catch {}
      }
      this._previousFocus = null;
    }

    /* Gouden Lot uitleg-modal — opent vanuit "Hoe werkt het" stap 3.
       Statische uitleg (geen portal data) want het concept zelf is vast. */
    openGoudenLotModal() {
      if (!this.goudenLotModal) return;
      this._previousFocus = document.activeElement;
      this.goudenLotModal.hidden = false;
      requestAnimationFrame(() => {
        this.goudenLotModal.setAttribute('data-open', '');
        this.goudenLotModal.querySelector('[data-wheel-gouden-lot-close]')?.focus?.();
      });
    }

    closeGoudenLotModal() {
      if (!this.goudenLotModal) return;
      this.goudenLotModal.removeAttribute('data-open');
      setTimeout(() => { this.goudenLotModal.hidden = true; }, 300);
      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        try { this._previousFocus.focus(); } catch {}
      }
      this._previousFocus = null;
    }

    /* Pull the prize names out of the portal description prose.
       Looks for the "kans op …" sentence and splits on commas / "of" /
       "én". Returns capitalised fragments — falls back to empty if the
       prose doesn't match the expected pattern. */
    parseGoldenTicketBullets(description) {
      if (!description) return [];
      const match = description.match(/kans op[:\s]+([^.]+)/i);
      if (!match) return [];

      return match[1]
        .split(/,\s*|\s+of\s+|\s+én\s+/i)
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => s.charAt(0).toUpperCase() + s.slice(1));
    }

    renderRevealProduct(wrap, prize) {
      if (!wrap) return;
      const variant = prize?.shopifyVariant;
      const isShopify = prize?.type === 'shopify_variant' && variant;

      if (!isShopify) {
        wrap.hidden = true;
        return;
      }

      const imgEl = wrap.querySelector('[data-wheel-reveal-product-image]');
      const variantEl = wrap.querySelector('[data-wheel-reveal-product-variant]');

      if (imgEl) {
        if (variant.image) {
          imgEl.src = variant.image;
          imgEl.alt = variant.productTitle || prize.label || '';
          imgEl.style.display = '';
        } else {
          imgEl.removeAttribute('src');
          imgEl.style.display = 'none';
        }
      }

      if (variantEl) {
        const vTitle = variant.variantTitle;
        if (vTitle && vTitle !== 'Default Title') {
          variantEl.textContent = vTitle;
          variantEl.hidden = false;
        } else {
          variantEl.hidden = true;
        }
      }

      wrap.hidden = false;
    }

    closeReveal() {
      this.reveal.removeAttribute('data-open');
      this.state.isSpinning = false;
      this.stage.classList.remove('is-spinning', 'is-zoomed');

      // Single-spin model: na een succesvolle spin heeft markOrderSpent
      // de order al uit de lijst gehaald en spinBtn disabled. Niets om
      // hier nog te herstellen — alleen focus terugzetten.

      // Restore focus for keyboard users
      if (this._previousRevealFocus && typeof this._previousRevealFocus.focus === 'function') {
        try { this._previousRevealFocus.focus(); } catch {}
      }
      this._previousRevealFocus = null;

      // Reset wheel for the next spin (smooth zoom-out)
      this.animateReset();
      setTimeout(() => { this.reveal.hidden = true; }, 300);
    }

    animateReset() {
      const startRotation = this.state.rotation;
      const startZoom = this.state.zoom;
      const duration = 800;
      const startTime = performance.now();
      const frame = (now) => {
        const t = clamp((now - startTime) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        this.state.zoom = lerp(startZoom, 1, eased);
        // keep rotation where it landed — don't spin back
        this.draw();
        if (t < 1) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    }

    copyDebug(btn) {
      const meta = document.querySelector('[data-wheel-debug-meta]')?.textContent || '';
      const body = document.querySelector('[data-wheel-debug-body]')?.textContent || '';
      const payload = `=== GOLDEN WHEEL DEBUG ===\n${meta}\n\n${body}`;
      const originalText = btn.textContent;

      const flash = (msg, ms = 1800) => {
        btn.classList.add('is-copied');
        btn.textContent = msg;
        setTimeout(() => {
          btn.classList.remove('is-copied');
          btn.textContent = originalText;
        }, ms);
      };

      // Try modern API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(payload).then(
          () => flash('✓ Gekopieerd'),
          () => this.copyDebugFallback(payload, flash)
        );
      } else {
        this.copyDebugFallback(payload, flash);
      }
    }

    copyDebugFallback(payload, flash) {
      // Fallback: temp textarea + execCommand
      try {
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, payload.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          flash('✓ Gekopieerd');
          return;
        }
      } catch (e) { /* fall through */ }

      // Last resort: show a selectable textarea the user can copy manually
      const bodyEl = document.querySelector('[data-wheel-debug-body]');
      if (!bodyEl) return;
      const existing = document.querySelector('[data-wheel-debug-manual]');
      if (existing) { existing.remove(); flash('✖ Probeer opnieuw'); return; }
      const ta = document.createElement('textarea');
      ta.setAttribute('data-wheel-debug-manual', '');
      ta.value = payload;
      ta.readOnly = true;
      ta.style.cssText = 'width:100%;min-height:200px;margin-top:12px;padding:10px;background:#0a0a0a;color:#e0e0e0;border:1px dashed #9a722e;border-radius:6px;font-family:ui-monospace,monospace;font-size:11px;';
      bodyEl.insertAdjacentElement('afterend', ta);
      ta.focus();
      ta.select();
      flash('👇 Selecteer + kopieer handmatig (Ctrl+C)');
    }

    /* Tear down all timers, RAFs, observers and document-level listeners.
       Called when Shopify reloads/unloads this section in the theme
       editor or when the page is replaced via SPA-style nav. Idempotent —
       safe to call multiple times. */
    destroy() {
      if (this._destroyed) return;
      this._destroyed = true;

      if (this._idleRAF != null) {
        cancelAnimationFrame(this._idleRAF);
        this._idleRAF = null;
      }
      if (this.state?.countdownInterval) {
        clearInterval(this.state.countdownInterval);
        this.state.countdownInterval = null;
      }
      if (this._wedgeFadeRAF != null) {
        cancelAnimationFrame(this._wedgeFadeRAF);
        this._wedgeFadeRAF = null;
      }
      if (this._ticketPulseTimer) {
        clearTimeout(this._ticketPulseTimer);
        this._ticketPulseTimer = null;
      }
      if (this._resizeObserver) {
        try { this._resizeObserver.disconnect(); } catch {}
        this._resizeObserver = null;
      }
      if (this._intersectionObserver) {
        try { this._intersectionObserver.disconnect(); } catch {}
        this._intersectionObserver = null;
      }
      if (this._escHandler) {
        document.removeEventListener('keydown', this._escHandler);
        this._escHandler = null;
      }
      // Cancel pending image loads so they don't trigger redraws after teardown
      if (this.imageCache) {
        for (const img of this.imageCache.values()) {
          if (img && img instanceof Image && !img.complete) {
            img.onload = null;
            img.onerror = null;
            try { img.src = ''; } catch {}
          }
        }
        this.imageCache.clear();
      }
      // Drop any focus refs so the GC isn't holding old DOM nodes
      this._previousFocus = null;
      this._previousRevealFocus = null;
    }

    markOrderSpent(orderId /*, wonPrizeLabel */) {
      // Drop locally voor instant feedback — backend zal 'm sowieso niet
      // meer terugsturen in eligibleOrders. loadRemoteConfig() volgt
      // elders (post-spin error path of caller) voor autoritatieve sync.
      this.state.orders = (this.state.orders || []).filter(
        o => String(o.id) !== String(orderId)
      );
      this.renderOrders();
      this.state.selectedOrderId = null;
      this.state.goldMode = false;
      this.renderRiggedBanner();
      this.stage?.classList.remove('is-rigged');
      this.spinBtn.disabled = true;
      this.setSpinIdleLabel();
    }

    /* "Kies een bestelling" if there's at least one order, otherwise
       "Geen bestelling beschikbaar". Backend pre-filtert, dus elke order
       in de lijst is per definitie draaibaar. */
    setSpinIdleLabel() {
      if (!this.spinLabel) return;
      const hasOrders = (this.state.orders || []).length > 0;
      this.spinLabel.textContent = hasOrders
        ? 'Kies een bestelling'
        : 'Geen bestelling beschikbaar';
    }
  }

  /* -------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function boot() {
    document.querySelectorAll('[data-golden-wheel]').forEach(el => {
      // Tear down any previous instance bound to this node (theme-editor
      // section reorder, Shopify section:load, SPA-style nav). Otherwise
      // every reorder leaks a setInterval + setTimeout chain.
      if (el._goldenWheel && typeof el._goldenWheel.destroy === 'function') {
        el._goldenWheel.destroy();
      }
      const inst = new GoldenWheel(el);
      el._goldenWheel = inst;
      inst.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme-editor: re-init when a section is loaded/reordered
  document.addEventListener('shopify:section:load', (e) => {
    const node = e.target?.querySelector?.('[data-golden-wheel]');
    if (node) boot();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const node = e.target?.querySelector?.('[data-golden-wheel]');
    if (node && node._goldenWheel) {
      node._goldenWheel.destroy();
      node._goldenWheel = null;
    }
  });
})();
